import {ESerialMode, FactoryRegistry, Serializable} from "lib/commons/io/serial";
import {DOM, ENodeType, EUnknownNodeType} from "lib/commons/xml/dom";
import {IJmlNode, IJmlObj, IJmlSet, JML} from "lib/commons/xml/jml";
import {XA} from "lib/commons/xml/xAddr";
import {XmlDeleteMsg, XmlInsertMsg, XmlStrMsg} from "lib/edit/ot/xmlHouse";
import {CARD, ECard, EPastePos, Schema, SkContext, SkNode} from "lib/edit/schema/schema";
import {EAnnotLevel, EDirectiveType, EFuzzyType, ISkStructDef, SkAnnotAttrMissing, SkAnnotAttrUnknown, SkAnnotEltUnknown, SkAnnotMissing, SkAnnotTextForbidden, SkAnnotWrongValue} from "lib/edit/schema/schemaAnnots";
import {ISkPatternResolver} from "lib/edit/schema/schemaBuilder";
import {SkMatcherAnyAttr, SkMatcherAnyElt, SkMatcherAttr, SkMatcherComment, SkMatcherDoc, SkMatcherNode, SkMatcherText} from "lib/edit/schema/schemaMatchers";
import {ISkMetaNode, SKMETALIB} from "lib/edit/schema/schemaMeta";
import {IConverterProvider} from "lib/edit/import/convert";
import {helpId} from "back/help/helpApi";

type ISkRuleFilter = (rule: SkRule) => boolean

/** SkRule réprésentant un node ou un attribut du DOM, ie pas une directive. */
export type ISkRuleObj = SkRuleNode | SkRuleAttr | SkRuleComment

/**
 * Règle d'analyse et de traitements d'un noeud dom en fonction d'un Schéma.
 */
export abstract class SkRule extends Serializable implements ISkStructDef {
	static readonly registry = new FactoryRegistry<SkRule>("SkRule");

	/**
	 * Nom de cette structure à titre informatif (log, debug...),
	 * n'est pas un identifiant unique fiable (cf uniqueName).
	 */
	structName: string;
	structLabel?: string;
	structType: ENodeType | EFuzzyType | EDirectiveType;

	/**
	 * Méta-modèle définis pour les éléments (SkRuleElt), les attributs (SkRuleAttr) et les comments (SkRuleComment).
	 * Les noeuds textes n'ont pas de méta-modèles, dû aux pbs de fragmentation non signifiante des noeuds textes;
	 * la méta-modélisation des noeuds textes est portée par l'élément container.
	 */
	skMeta: ISkMetaNode;

	skFamily?:
		/** Familles des balises de blocks. */
		'sub-level' | 'content' | 'property' | 'property/title' | 'property/url'
		/** Familles des balises inline fondées sur la sémantique HTML. */
		| 'a' | 'b' | 'cite' | 'code' | 'em' | 'i' | 'kbd' | 'q' | 'samp' | 'small' | 'strong' | 'sub' | 'sup';

	skMalusCoef?: number;

	initSkMeta(skMeta: ISkMetaNode): this {
		this.skMeta = skMeta;
		return this;
	}

	abstract structMatch(nodeType: ENodeType | EUnknownNodeType, nodeName?: string): boolean;

	/**
	 *
	 * @param guardStack usage interne, controle de récursivité.
	 */
	abstract createContent(childrenToAppend?: IJmlSet, attsToAppend?: IJmlObj, guardStack?: SkRuleElt[]): void | string;

	/**
	 * Identifiant unique de la directive : signifie que cette Rule peut être réutilisée,
	 * et donc optimisation par fusion avec un contexte parent impossible.
	 */
	uniqueName?: string;

	/** Surcharge locale à cette Rule de la règle pour les elts inconnus. */
	unknownEltRule?: SkRule;
	/** Surcharge locale à cette Rule de la règle pour les noeuds textes interdits. */
	forbiddenTextRule?: SkRule;
	/** Surcharge locale à cette Rule de la règle pour les attributs inconnus. */
	unknownAttrRule?: SkRule;
	/** Surcharge locale à cette Rule de la règle pour les comments. */
	commentRule?: SkRule;

	/**
	 * Cardinalité de cette rule.
	 */
	get card(): ECard {return '1'}

	set card(card: ECard) {}

	/**
	 * Recherche le 1er {SkRuleNode} matchant node pour initier une association avec ce noeud.
	 * @param node
	 * @return Retourne null si cette rule ne contient aucun {SkRuleNode} matchant node.
	 */
	findRuleNodeFor(node: Node | IJmlNode): SkRuleNode {return null}

	/**
	 * Recherche le 1er {SkRuleAttr} matchant attr pour initier une association avec cet attribut.
	 * @param attr
	 * @return Retourne null si cette rule ne contient aucun {SkRuleAttr} matchant node.
	 */
	findRuleAttrFor(attr: string): SkRuleAttr {return null}

	/**
	 * Recherche une Rule parmi l'arbre des Rules, en traversant les directives.
	 */
	findRule(filter: ISkRuleFilter, includeDirectives?: boolean): SkRule | null {return filter(this) ? this : null}

	/**
	 * Recherche une Rule parmi l'arbre des Rules, en traversant les directives, sans jamais les inclure.
	 */
	findRules(filter: ISkRuleFilter, result?: ISkRuleObj[]): ISkRuleObj[] | undefined {
		if (filter(this)) (result || (result = [])).push(this as any);
		return result;
	}

	/**
	 * Parcours les rules en peuplant en paralèlle une stack des directives ancêtres.
	 * Si filter retourne true, le parcours est interrompu.
	 */
	scanRules(filter: ISkRuleFilter, directives: SkRuleDirective[], includeDirectives?: boolean): SkRule | null {return filter(this) ? this : null}

	/**
	 * Recherche ou construit un SkRule définissant ce qui peut être inséré après from.
	 * this doit être une Rule racine du contenu du contexte parent (rule autonome intégrant sa cardinalité).
	 *
	 * @param from doit exister dans le content de this
	 * @return null si aucune insertion possible.
	 */
	buildRuleAfter(from: ISkRuleObj, ctxCard: ECard = '1'): SkRule {
		const realCard = CARD.combineCard(this.card, ctxCard);
		return CARD.isRepeatable(realCard) ? this : SkRuleEmpty.SINGLETON;
	}

	/** Retourne la card d'une subRule ou null si la rule n'est ni this ni dans la descendance des directives. */
	getRealCardSubRule(rule: SkRule): ECard | undefined {
		return this === rule ? this.card : undefined;
	}

	isEltRemovable(elt: Element): boolean {
		if (this.card === '+') {
			//removable si on n'est pas le dernier.
			let sib = elt.previousElementSibling;
			if (sib && this.structMatch(ENodeType.element, sib.nodeName)) return true;
			sib = elt.nextElementSibling;
			if (sib && this.structMatch(ENodeType.element, sib.nodeName)) return true;
			return false;
		}
		return CARD.isOptionnal(this.card);
	}

	/**
	 * 1ère étape du processus de validation. Partant d'un scope de noeuds fils et d'attributs,
	 * recherche les meilleures associations possibles aux règles. Cette méthode exploite skCtx
	 * pour mémoriser la solution optimale.
	 *
	 * La 2ème étape du processus de validation consiste à binder les éléments fils et exécuter les actions
	 * suceptibles de l'être dès leur binding (cf SkRuleNode#execActionsOnBinding() et SkRuleAttr#execActionsOnBinding())
	 * Cette 2ème étape est réalisée par SkContext#bindRulesAndExecute().
	 *
	 * Pour la 3ème et dernière étape, cf Rule.execActions().
	 *
	 * @param skCtx Contexte d'exécution
	 * @param matchMode Mode de matching, cf SkMatchMode.
	 * @param contextCard Cardinalité du contexte parent à combiner avec sa propre card: CARD.combineCard(contextCard, this.card)
	 */
	findBestMatching(skCtx: SkContext, matchMode = ESkMatchMode.all, contextCard: ECard = '1') {}

	/**
	 * Utilisé par les directives (choice, group, unorderedGroup) pour évaluer si une sous-règle correspond au contexte courant
	 * (ie, attributs restant à binder ou skCtx.currentChild).
	 *
	 * @param skCtx
	 * @return {boolean}
	 */
	isMatching(skCtx: SkContext): boolean {return false}

	/**
	 * 3ème étape du processus de validation (voir #findBestMatching() pour les étapes 1 et 2).
	 *
	 * Exécute les actions relative à cette rule.
	 * Attention, les directives doivent ré-appeler ses subRules dans le meme ordre que findBestMatching() pour que
	 * le principe de pile mémoire soit opérationnel.
	 *
	 * @param skCtx Contexte d'execution.
	 * @param matchMode Mode de matching, cf SkMatchMode.
	 * @param contextCard Cardinalité du contexte parent à combiner avec sa propre card: CARD.combineCard(contextCard, this.card)
	 */
	execActions(skCtx: SkContext, contextCard: ECard = '1') {}

	xAppendInsertables(skCtx: SkContext, listNodes: ISkStructDef[], listAttrs?: ISkStructDef[]) {}

	getUnknownNodeRule(skNode: SkNode, node: Node | IJmlNode): SkRuleNode {
		switch (node instanceof Node ? node.nodeType : JML.jmlNode2nodeType(node)) {
		case ENodeType.element:
			if (this.unknownEltRule) {
				const rule = this.unknownEltRule.findRuleNodeFor(node);
				if (rule) return rule;
			}
			return skNode.schemaDom.schema.unknownEltRule.findRuleNodeFor(node);
		case ENodeType.text:
			if (DOM.WHITESPACES.test(node instanceof Node ? node.nodeValue : node as string)) {
				//Au cas où la normalisation du flux XML n'a pas permis de suppr les noeuds WS dû à la présence d'un xml:space=preserve
				// ex: metas dans les balises inline d'un para, le flux XML ayant été indenté par un outil non respectueux du xml:space=preserve.
				return SkRuleTextWs.DEFAULT;
			}
			if (this.forbiddenTextRule) {
				const rule = this.forbiddenTextRule.findRuleNodeFor(node);
				if (rule) return rule;
			}
			return skNode.schemaDom.schema.forbiddenTextRule.findRuleNodeFor(node);
		case ENodeType.comment:
			if (this.commentRule) {
				const rule = this.commentRule.findRuleNodeFor(node);
				if (rule) return rule;
			}
			return skNode.schemaDom.schema.commentRule.findRuleNodeFor(node);
		}
	}

	getUnknownAttRule(skNode: SkNode, attr: string): SkRuleAttr {
		if (this.unknownAttrRule) {
			const rule = this.unknownAttrRule.findRuleAttrFor(attr);
			if (rule) return rule;
		}
		return skNode.schemaDom.schema.unknownAttrRule.findRuleAttrFor(attr);
	}

	getRealCardSubNode(nodeType: ENodeType, nodeName: string): ECard | undefined {
		return this.structMatch(nodeType, nodeName) ? this.card : undefined;
	}

	checkSchemaOrder(eltName1: string, eltName2: string): boolean | undefined {return undefined}

	/** Linkaged des rules qui référencent d'autres rules par leur name. */
	linkRules(map: ISkPatternResolver): SkRule {
		return this;
	}


	writeTo(o: any, mode: ESerialMode) {
		if (this.uniqueName != null) o.un = this.uniqueName;
	}

	readFrom(o: any, mode: ESerialMode): this {
		if (o.un != null) this.uniqueName = o.un;
		return this;
	}


	/**
	 * Id unique de cette Rule pour la sérialisation et la désérialisation du schéma.
	 * Les patterns singletons n'ont jamais d'id (undefined).
	 * A la sérialisation, si l'id === undefined, la Rule sera sérialisée inline.
	 * ATTENTION : calculé que lors de la sérialisation du schéma contenant cette rule.
	 * ==> API sérialisation à ne pas utiliser hors sérialisation du schéma complet
	 */
	id: number | undefined;

	/** On ne peut pas utiliser ici Serializable.toString() car this.id non calculé => cycle infini. */
	toString(): string {return this.structName}

	/** Indexation des patterns d'un schéma. */
	abstract indexPatterns(rulesIndex: SkRule[]): void;

	/** Sérialisation d'un Pattern en inline ou par son id. */
	static serialRefRule(r: SkRule, mode: ESerialMode): any {
		if (r.id !== undefined) return r.id;
		return Serializable.serialSerializable(r, mode);
	}

	/** Désérialisation d'un Pattern en inline ou par son id. */
	static deserialRefRule(r: any, mode: ESerialMode): SkRule | number {
		if (typeof r === 'number') return r;
		return Serializable.deserialSerializable(r, mode, SkRule.registry);
	}

}

/**
 * Mode d'exécution pour SkRule#findBestMatching(skCtx, matchMode, contextCard) et SkRule#execActions(skCtx, contextCard).
 */
enum ESkMatchMode {
	/** Match tous les nodes et attributs, y compris ceux inconnus en les associant au SkRule unkonwn. */
	all,

	/** Match le(s) premier(s) noeuds jusqu'au 1er qui ne match plus et match les attributs reconnus. */
	ordered
}

/**
 * Règle représentant un noeud concret (Document, Element, Text).
 */
export abstract class SkRuleNode extends SkRule {

	/**
	 * Règle à appliquer pour le contenu de ce noeud constitué de ses attributs et de ses noeuds fils.
	 * Sauf cas trivial contenant un seul attr ou elt fils, cette règle est généralement une directive.
	 */
	contentRule: SkRule;

	/** Ref temporaire au contenu avant linkage pouvant pointer la contentRule par son name. */
	tmpCtRule: string | number | SkRule;

	/**
	 * Matcher de cette règle avec le noeud auquel il pourrait être associé.
	 */
	matcher: SkMatcherNode;

	initContentRule(contentRule: SkRule = SkRuleEmpty.SINGLETON): this {
		this.contentRule = contentRule;
		this.tmpCtRule = undefined;
		return this;
	}

	initContentName(contentRule: string | SkRule): this {
		this.tmpCtRule = contentRule;
		return this;
	}

	linkRules(map: ISkPatternResolver): SkRule {
		if (this.tmpCtRule === null) return this; //linkRules() déjà appelé (pb récursivité).
		if (this.tmpCtRule !== undefined) this.contentRule = map.resolveRule(this.tmpCtRule);
		this.tmpCtRule = null; //marque le linkRule done (pb récursivité).
		if (this.contentRule) this.contentRule = this.contentRule.linkRules(map);
		return this;
	}

	// @ts-ignore
	get structName() {return this.matcher.techName}

	createContent(childrenToAppend?: IJmlSet, attsToAppend?: IJmlObj, guardStack?: SkRuleElt[]): void | string {}

	/**
	 * Suite à l'association de cette règle à un noeud, exécute les actions relative à cette rule.
	 * Attention, la pile mémoire du contexte ne peut être exploitée ici.
	 * Méthode utilisé par les rules de type unknown qui ne sont pas appelées dans l'étape SkRule.execActions().
	 * @param {SkContext} skCtx Contexte d'execution
	 * @param {Node} node Noeud sur lequel cette règle est associée.
	 */
	execActionsOnBinding(skCtx: SkContext, node: Node) {
	}

	findRuleNodeFor(node: Node | IJmlNode): SkRuleNode {
		if (node instanceof Node) return this.matcher.matchNode(node.nodeType, node.nodeName) && this.deepMatch(node) ? this : null;
		return this.matcher.matchNode(JML.jmlNode2nodeType(node), JML.jmlNode2name(node)) && this.deepMatch(node) ? this : null;
	}

	isMatching(skCtx: SkContext) {
		const node = skCtx.currentChild;
		return node ? this.matcher.matchNode(node.nodeType, node.nodeName) : false;
	}

	deepMatch(node: Node | IJmlNode): boolean {return true}

	indexPatterns(rulesIndex: SkRule[]) {
		if (Object.isFrozen(this)) return;
		if (this.id === undefined) {
			this.id = rulesIndex.length;
			rulesIndex.push(this);
			if (this.contentRule) this.contentRule.indexPatterns(rulesIndex);
		}
	}
}

/**
 * Règles non concrètes (choice, group, unorderedGroup, empty) spécifiant les agencements possibles du contenu
 * d'un {SkRuleNode}.
 */
export abstract class SkRuleDirective extends SkRule {

	subRules: SkRule[];
	subCards: ECard[];

	/** Tableau temporaire avant linkage pouvant pointer les subRules par leur name. */
	tmpSubRules: Array<string | number | SkRule>;

	/** Name de la directive construit automatiquement. */
	protected _builtName: string;

	protected _card: ECard = '1';

	initName(name: string): this {
		this.uniqueName = name;
		return this;
	}

	initCard(card: ECard): this {
		this._card = card;
		return this;
	}

	initSubRules(subRules: SkRule[]): this {
		this.subRules = subRules;
		return this;
	}

	initSubNames(subNames: Array<string | SkRule>): this {
		this.tmpSubRules = subNames;
		return this;
	}

	addSubName(subName: string | SkRule): this {
		if (!this.tmpSubRules) this.tmpSubRules = [subName];
		else this.tmpSubRules.push(subName);
		return this;
	}

	structMatch(nodeType: ENodeType, nodeName?: string): boolean {
		for (let i = 0, s = this.subRules.length; i < s; i++) if (this.subRules[i].structMatch(nodeType, nodeName)) return true;
		return false;
	}

	linkRules(map: ISkPatternResolver): SkRule {
		if (this.tmpSubRules) {
			this.subRules = this.tmpSubRules.map(r => map.resolveRule(r));
			this.tmpSubRules = undefined;
			this.subRules.forEach((r, i) => {
				let rule = r.linkRules(map);
				while (rule instanceof SkRuleDirective && !rule.structLabel && rule.subRules.length === 1 && !rule.uniqueName && !Object.isFrozen(rule)) {
					this.setOverSubCard(i, this.overSubCard(i, rule.card));
					rule = rule.subRules[0];
				}
				this.subRules[i] = rule;
			});
			if (this.subRules.length === 1 && !this.structLabel) {
				const rule = this.subRules[0];
				if (!rule.uniqueName && !Object.isFrozen(rule)) {
					//Optimisation possible : suppr de cette directive inutile par fusion de la cardinalité.
					rule.card = CARD.combineCard(this.card, rule.card);
					rule.uniqueName = this.uniqueName;
					return rule;
				}
			}
		}
		return this;
	}

	get card() {return this._card}

	set card(card: ECard) {
		this._card = card
	}


	findRuleNodeFor(node: Node | IJmlNode): SkRuleNode {
		for (let i = 0, s = this.subRules.length; i < s; i++) {
			const found = this.subRules[i].findRuleNodeFor(node);
			if (found) return found;
		}
		return null;
	}

	findRuleAttrFor(attr: string): SkRuleAttr {
		for (let i = 0, s = this.subRules.length; i < s; i++) {
			const found = this.subRules[i].findRuleAttrFor(attr);
			if (found) return found;
		}
		return null;
	}

	findRule(filter: ISkRuleFilter, includeDirectives?: boolean): SkRule | null {
		if (includeDirectives && filter(this)) return this;
		for (let i = 0, s = this.subRules.length; i < s; i++) {
			const result = this.subRules[i].findRule(filter, includeDirectives);
			if (result != null) return result;
		}
		return null;
	}

	findRules(filter: ISkRuleFilter, result?: ISkRuleObj[]): ISkRuleObj[] {
		//if (filter(this)) result.push(this); intérêt ?
		for (let i = 0, s = this.subRules.length; i < s; i++) {
			result = this.subRules[i].findRules(filter, result);
		}
		return result;
	}

	scanRules(filter: ISkRuleFilter, directives: SkRuleDirective[], includeDirectives?: boolean): SkRule | null {
		if (includeDirectives && filter(this)) return this;
		directives.push(this);
		for (let i = 0, s = this.subRules.length; i < s; i++) {
			const result = this.subRules[i].scanRules(filter, directives, includeDirectives);
			if (result != null) return result;
		}
		directives.pop();
		return null;
	}

	getRealCardSubRule(rule: SkRule): ECard | undefined {
		if (this === rule) return this.card;
		for (let i = 0, s = this.subRules.length; i < s; i++) {
			const card = this.subRules[i].getRealCardSubRule(rule);
			if (card != null) return CARD.combineCard(this.overSubCard(i, this.card), card);
		}
		return undefined;
	}

	getRealCardSubNode(nodeType: ENodeType, nodeName: string): ECard | undefined {
		for (let i = 0, s = this.subRules.length; i < s; i++) {
			const subR = this.subRules[i];
			if (subR.structMatch(nodeType, nodeName)) {
				const subCard = subR.getRealCardSubNode(nodeType, nodeName);
				return CARD.combineCard(this.card, this.overSubCard(i, subCard));
			}
		}
		return undefined;
	}

	isEltRemovable(elt: Element): boolean {
		switch (this.card) {
		case '?':
		case '*':
			return true;
		case '+':
			return super.isEltRemovable(elt);
		default : // '1'
			//on descend
			for (let i = 0, s = this.subRules.length; i < s; i++) {
				const subR = this.subRules[i];
				if (subR.structMatch(ENodeType.element, elt.nodeName)) {
					return subR.isEltRemovable(elt);
				}
			}
			console.trace("Should never occur"); //Le contexte amont aurait du détecter que elt ne matchait pas cette rule.
			return false;
		}
	}

	checkSchemaOrder(eltName1: string, eltName2: string): boolean | undefined {
		for (let i = 0, s = this.subRules.length; i < s; i++) {
			const subR = this.subRules[i];
			//Si les 2 names sont dans le même fils on interroge le fils
			//Dès qu'un des noms match une rule fils et pas l'autre, ordre impossible à connaitre.
			const inSub1 = subR.structMatch(ENodeType.element, eltName1);
			if (subR.structMatch(ENodeType.element, eltName2)) {
				if (inSub1) return subR.checkSchemaOrder(eltName1, eltName2);
				return undefined;
			} else {
				if (inSub1) return undefined;
			}
		}
		return undefined;
	}

	protected overSubCard(i: number, contextCard: ECard): ECard {
		if (!this.subCards) return contextCard;
		const card = this.subCards[i];
		if (!card) return contextCard;
		return CARD.combineCard(contextCard, card);
	}

	protected setOverSubCard(i: number, card: ECard) {
		if (card == '1') return;
		if (!this.subCards) this.subCards = [];
		this.subCards[i] = card;
	}


	indexPatterns(rulesIndex: SkRule[]) {
		if (Object.isFrozen(this)) return;
		if (this.id === undefined) {
			this.id = rulesIndex.length;
			rulesIndex.push(this);
			this.subRules.forEach(r => {
				r.indexPatterns(rulesIndex)
			});
		}
	}

	writeTo(o: any, mode: ESerialMode) {
		super.writeTo(o, mode);
		o.rules = this.subRules.map(r => SkRule.serialRefRule(r, mode));
		if (this.subCards != null) o.cards = Serializable.serialArrayPrimitives(this.subCards, mode);
		o.card = this._card;
	}

	readFrom(o: any, mode: ESerialMode): this {
		super.readFrom(o, mode);
		if (o.un != null) this.uniqueName = o.un;
		this.tmpSubRules = o.rules.map((m: any) => SkRule.deserialRefRule(m, mode));
		if (o.cards != null) this.subCards = o.cards;
		this._card = o.card;
		return this;
	}
}

/**
 * Règle associée à un noeud de type Document.
 */
export class SkRuleDoc extends SkRuleNode {
	static readonly type = "/";

	init(contentRule: SkRule, skMeta = SKMETALIB.getMetaNode('/'), matcher: SkMatcherNode = SkMatcherDoc.SINGLETON): this {
		this.contentRule = contentRule;
		this.matcher = matcher;
		this.skMeta = skMeta;
		return this;
	}

	// @ts-ignore
	get structType() {return ENodeType.document}

	structMatch(nodeType: ENodeType, nodeName?: string): boolean {return nodeType === ENodeType.document}

	//SkRuleDoc n'est jamais une règle descendante d'un SkRuleNode.contentRule
	findBestMatching(skCtx: SkContext, matchMode: ESkMatchMode, contextCard: ECard) {
		throw Error("Should never be called!");
	}

	isMatching(skCtx: SkContext): boolean {
		throw Error("Should never be called!");
	}

	execActions(skCtx: SkContext, contextCard: ECard) {
		throw Error("Should never be called!");
	}

	createContent(childrenToAppend?: IJmlSet, attsToAppend?: IJmlObj): void | string {
		this.contentRule.createContent(childrenToAppend, attsToAppend);
	}

	writeTo(o: any, mode: ESerialMode) {
		super.writeTo(o, mode);
		if (this.matcher !== SkMatcherDoc.SINGLETON) o.matcher = Serializable.serialSerializable(this.matcher, mode);
		o.ctRule = SkRule.serialRefRule(this.contentRule, mode);
	}

	readFrom(o: any, mode: ESerialMode): this {
		super.readFrom(o, mode);
		this.matcher = o.matcher ? Serializable.deserialSerializable(this.matcher, mode, SkMatcherNode.registry) : SkMatcherDoc.SINGLETON;
		this.tmpCtRule = SkRule.deserialRefRule(o.ctRule, mode);
		return this;
	}
}

SkRule.registry.register(SkRuleDoc);

/**
 * Règle correspondant à un élément.
 */
export class SkRuleElt extends SkRuleNode {
	static readonly type = "*";

	/** Ensemble des convertisseurs susceptibles de produire un tel élément racine. */
	public convertFrom: IConverterProvider;

	/** Entrée d'aide. */
	helpId?: helpId

	protected _card: ECard;

	/** Bloque l'autoComplete même si cet élément est required. */
	noAutoComplete: boolean;

	init(matcher: SkMatcherNode, card: ECard = '1', contentRule?: SkRule): this {
		this.matcher = matcher;
		this._card = card;
		this.contentRule = contentRule || SkRuleEmpty.SINGLETON;
		return this;
	}

	get card(): ECard {return this._card}

	set card(card: ECard) {
		this._card = card;
	}

	// @ts-ignore
	get structType() {return this.matcher.isFuzzy() ? EFuzzyType.elements : ENodeType.element}

	isMatching(skCtx: SkContext) {
		const node = skCtx.currentChild;
		return node ? this.matcher.matchNode(node.nodeType, node.nodeName) && this.deepMatch(node as Element) : false;
	}

	isMatchNode(node: Node) {
		return this.matcher.matchNode(node.nodeType, node.nodeName) && this.deepMatch(node as Element);
	}

	isSameRoot(other: SkRuleElt): boolean {
		return this.matcher.startName === other.matcher.startName;
	}

	deepMatch(node: Node | IJmlNode): boolean {
		return this.skMeta ? this.skMeta.ruleMatch(this, node) : true;
	}

	createContent(childrenToAppend?: IJmlSet, attsToAppend?: IJmlObj, guardStack?: SkRuleElt[]): void | string {
		if (guardStack && this.noAutoComplete) return; //on n'est le root de la demande de construction et on refuse l'autoComplete, on sort.
		if (this.matcher.isFuzzy()) return this.matcher.startName;
		const elt = {'': this.matcher.startName};
		childrenToAppend.push(elt);
		if (CARD.isRequired(this.contentRule.card)) {
			const children: IJmlSet = [];
			if (guardStack) {
				if (guardStack.indexOf(this) >= 0) throw Error(`Infinite cycle in schema (with required cardinality): ${guardStack.join('/')}/${guardStack[0]}`);
				guardStack.push(this);
			} else guardStack = [this];
			this.contentRule.createContent(children, elt, guardStack);
			if (children.length > 0) childrenToAppend.push(children);
			if (guardStack) guardStack.length--;
		}
		//this.skMeta.
	}

	/** Crée un noeud et ses éventuels attributs différenciant. */
	createNode(parent: Node): Node {
		const result = (parent.ownerDocument || parent as Document).createElement(this.matcher.startName);
		if (this.skMeta && ('onCreateNode' in this.skMeta)) this.skMeta.onCreateNode(this, result);
		return result;
	}


	structMatch(nodeType: ENodeType, nodeName?: string): boolean {return this.matcher.matchNode(nodeType, nodeName)}

	findBestMatching(skCtx: SkContext, matchMode = ESkMatchMode.all, contextCard: ECard = '1') {
		const repeatable = CARD.isRepeatable(CARD.combineCard(contextCard, this.card));
		let found = 0;
		switch (matchMode) {
		case ESkMatchMode.all: {
			skCtx.forEachChild(ch => {
				if ((found == 0 || repeatable) && this.isMatchNode(ch)) {
					found++;
					skCtx.bindNodeToRule(ch, this);
				} else {
					skCtx.bindNodeToRule(ch, this.getUnknownNodeRule(skCtx.skNode, ch));
				}
			});
			skCtx.forEachAttr(a => {
				skCtx.bindAttrToRule(a, this.getUnknownAttRule(skCtx.skNode, a.nodeName));
			});
			break;
		}
		case ESkMatchMode.ordered:
			skCtx.forEachBindableChild(ch => {
				if ((found == 0 || repeatable) && this.isMatchNode(ch)) {
					found++;
					skCtx.bindNodeToRule(ch, this);
				} else return true; //on stop le parcours des fils.
			});
			break;
		}
		skCtx.pushRuleState(found);
	}

	execActions(skCtx: SkContext, contextCard: ECard = '1') {
		const found: number = skCtx.shiftRuleState();
		const card = CARD.combineCard(contextCard, this.card);
		const opts = skCtx.execOptions;
		if (opts.list1InsertableNodesAt !== undefined) {
			if (found === 0 || CARD.isRepeatable(card)) {
				const min = skCtx.currentOffsetMin;
				const max = skCtx.getOffsetMaxAfterCurrent(found);
				const insPoint1 = opts.list1InsertableNodesAt;
				if (insPoint1 === -1 || (min <= insPoint1 && max >= insPoint1)) {
					this.xAppendInsertables(skCtx, opts.insertableNodes1);
				}
				const insPoint2 = opts.list2InsertableNodesAt;
				if (insPoint2 >= 0 && min <= insPoint2 && max >= insPoint2) {
					this.xAppendInsertables(skCtx, opts.insertableNodes2);
				}
			}
		}

		if (opts.importContent
			&& (opts.importContent instanceof SkRule ? this === opts.importContent : this.findRuleNodeFor(opts.importContent))) {
			if ('pasteOffsetMin' in opts) {
				const minIns = skCtx.currentOffsetMin;
				const maxIns = skCtx.getOffsetMaxAfterCurrent(found);
				const insBefore = opts.pasteOffsetMin >= minIns && opts.pasteOffsetMin <= maxIns;
				const insAfter = opts.pasteOffsetMax >= minIns && opts.pasteOffsetMax <= maxIns;
				if (insBefore || insAfter) {
					//Une insertion est possible
					if (opts.pasteOffsetMin === opts.pasteOffsetMax) {
						//Si importOffsetMin == importOffsetMax, on insère dans un gap, ou dans un contexte virtuel => replace
						opts.pastePos = EPastePos.replace;
					}
					if (CARD.isRepeatable(card)) {
						if (insBefore) {
							opts.pastePos = insAfter ? EPastePos.anywhere : EPastePos.before | EPastePos.replace;
						} else {
							opts.pastePos = EPastePos.replace | EPastePos.after;
						}
					} else if (found === 0) {
						opts.pastePos = insBefore ? EPastePos.before : EPastePos.after; //Si insBefore && insAfter -> EPastePos.before | EPastePos.after ?
					} else {
						opts.pastePos = EPastePos.replace;
					}
				}
			} else {
				if (CARD.isRepeatable(card)) {
					opts.importPos = {insertOffsetMin: skCtx.currentOffsetMin, insertOffsetMax: skCtx.getOffsetMaxAfterCurrent(found), replaceChildren: [skCtx.currentOffset]};
					let n = skCtx.currentChild as Element;
					for (let i = 1; i < found; i++) {
						n = n.nextElementSibling;
						opts.importPos.replaceChildren.push(DOM.computeOffset(n));
					}
				} else if (found > 0) {
					//trouvé, on remplace
					opts.importPos = {replaceChildren: [skCtx.currentOffset]};
				} else {
					//pas trouvé, on insère
					opts.importPos = {insertOffsetMin: skCtx.currentOffsetMin, insertOffsetMax: skCtx.getOffsetMaxAfterCurrent(found)};
				}
			}
		}
		let addFindOffsetAfter;
		if (opts.findOffsetNodeType && this.structMatch(opts.findOffsetNodeType, opts.findOffsetNodeName)) {
			switch (opts.findOffsetInGroup || (CARD.isRepeatable(card) ? opts.findOffsetIfExistAndCardN : 'reject')) {
			case "reject":
				opts.findOffset = found > 0 ? -1 : skCtx.currentOffsetMax;
				break;
			case "start":
				if (!('findOffset' in opts)) opts.findOffset = skCtx.currentOffset;
				break;
			case "end":
				addFindOffsetAfter = true;
			}
		}
		if (found === 0) {
			if (CARD.isRequired(card)) {
				if (opts.autoComplete && !this.noAutoComplete) {
					const content: IJmlSet = [];
					this.createContent(content, null);
					if (content.length > 0) opts.corrections.push(new XmlInsertMsg().init(XA.append(XA.from(skCtx.node), skCtx.currentOffset), content));
				} else if (opts.genAnnots) {
					skCtx.addAnnot(new SkAnnotMissing().init(SkAnnotMissing.TYPE_eltMissing, skCtx.node, skCtx.currentOffset, this));
				}
			}
		} else {
			for (let i = 0; i < found; i++) skCtx.currentChild = skCtx.currentChild.nextSibling;
		}
		if (addFindOffsetAfter) opts.findOffset = skCtx.currentOffsetMax;
	}

	xAppendInsertables(skCtx: SkContext, listNodes: ISkStructDef[], listAttrs?: ISkStructDef[]) {
		if (listNodes?.indexOf(this) < 0 && this.skMeta.isInsertable(skCtx.skNode, this)) listNodes.push(this);
	}

	writeTo(o: any, mode: ESerialMode) {
		super.writeTo(o, mode);
		o.matcher = Serializable.serialSerializable(this.matcher, mode);
		o.ctRule = SkRule.serialRefRule(this.contentRule, mode);
		o.card = this.card;
	}

	readFrom(o: any, mode: ESerialMode): this {
		super.readFrom(o, mode);
		this.matcher = Serializable.deserialSerializable(o.matcher, mode, SkMatcherNode.registry);
		this.tmpCtRule = SkRule.deserialRefRule(o.ctRule, mode);
		this._card = o.card;
		return this;
	}

}

SkRule.registry.register(SkRuleElt);

export class SkRuleEltUnknown extends SkRuleNode {
	static readonly type = "*?";

	initUnknown(matcher: SkMatcherNode = SkMatcherAnyElt.SINGLETON, skMeta = SKMETALIB.getMetaNode('*')): this {
		this.matcher = matcher;
		this.contentRule = this; //pour la récursivité de l'affectation des skNodes aux sous-noeuds inconnus.
		this.skMeta = skMeta;
		return this;
	}

	get card(): ECard {return '?'}

	// @ts-ignore
	get structType() {return EFuzzyType.elements}

	structMatch(nodeType: ENodeType, nodeName?: string): boolean {return this.matcher.matchNode(nodeType, nodeName)}

	//isMatching(skCtx){return false}
	//execActions(skCtx, contextCard) {}

	findBestMatching(skCtx: SkContext, matchMode = ESkMatchMode.all, contextCard: ECard = '1') {
		//Récusivité des élements inconnus
		skCtx.forEachChild(ch => {
			skCtx.bindNodeToRule(ch, this.getUnknownNodeRule(skCtx.skNode, ch));
		});
		skCtx.forEachAttr(a => {
			skCtx.bindAttrToRule(a, this.getUnknownAttRule(skCtx.skNode, a.nodeName));
		});
	}

	execActionsOnBinding(skCtx: SkContext, elt: Element) {
		if (skCtx.skNode.rule instanceof SkRuleEltUnknown) return; //Aucun traitement si on n'est pas l'elt unkonwn root.
		if (skCtx.execOptions.autoCleanup) skCtx.execOptions.corrections.push(new XmlDeleteMsg().init(XA.from(elt), 1));
		else if (skCtx.execOptions.genAnnots) skCtx.addAnnot(new SkAnnotEltUnknown().init(elt));
	}

	linkRules() {return this}

	writeTo(o: any, mode: ESerialMode) {
		if (this === SkRuleEltUnknown.DEFAULT) return;
		super.writeTo(o, mode);
		o.matcher = this.matcher !== SkMatcherAnyElt.SINGLETON ? Serializable.serialSerializable(this.matcher, mode) : null;
		if (this.contentRule !== this) o.ctRule = SkRule.serialRefRule(this.contentRule, mode);
	}

	readFrom(o: any, mode: ESerialMode): this {
		if (o.matcher === undefined) return SkRuleEltUnknown.DEFAULT as this;
		super.readFrom(o, mode);
		this.matcher = o.matcher ? Serializable.deserialSerializable(this.matcher, mode, SkMatcherNode.registry) : SkMatcherAnyElt.SINGLETON;
		if (o.ctRule) this.tmpCtRule = SkRule.deserialRefRule(o.ctRule, mode);
		else this.contentRule = this;
		return this;
	}

	static readonly DEFAULT = new SkRuleEltUnknown();
}

SkRule.registry.register(SkRuleEltUnknown);


/** Méthodes dédiées à la validation des strings (contenu des noeuds texte et des attributs). */
export interface ISkRuleStr extends SkRule {

	/** Evalue si cette string est valide (pour choice et Ugroup). */
	matchStr(str: string, contextCard: ECard): boolean;

	//matchStrList(strs: string[], contextCard: OCard): boolean;

	/**
	 * Evalue cette string et publie les annotations d'erreur.
	 * @param anchor Pour les noeuds texte, la validation est ancrée sur l'élément parent
	 * car il est interdit d'avoir un texte typé combiné avec d'autres éléments
	 * (http://relaxng.org/spec-20011203.html#string-sequences). Permet de traiter
	 * l'absence de noeuds textes ou un noued texte "" de façon transparente.
	 */
	validStr(str: string, contextCard: ECard, anchor: Element | Attr, skCtx: SkContext): void;

	//validStrList(strs: string[], contextCard: OCard, anchor: Node | Attr, skCtx: SkContext): void;

	getDefaultValue(): string;

}

export function isSkRuleStr(rule: SkRule): rule is ISkRuleStr {return rule && ('matchStr' in rule)}


/** Rule pour un noeud texte (exploité aussi pour valider le contenu d'un attribut, cf SkRuleAttr.valueCheck). */
export abstract class SkRuleStr extends SkRuleNode implements ISkRuleStr {

	// @ts-ignore
	get structType() {return ENodeType.text}

	structMatch(nodeType: ENodeType, nodeName?: string): boolean {return nodeType === ENodeType.text}

	// @ts-ignore
	get matcher(): SkMatcherNode {return SkMatcherText.SINGLETON}

	/**
	 * Les string successives seront concaténés et validées ensuite comme un noeud unique.
	 * On autorise donc toujours this en rule suivante même si card mono.
	 */
	buildRuleAfter(from: ISkRuleObj, ctxCard: ECard = '1'): SkRule {
		return this;
	}

	createContent(childrenToAppend?: IJmlSet, attsToAppend?: IJmlObj): void | string {
		childrenToAppend.push(this.getDefaultValue());
	}

	getDefaultValue(): string {return ""}

	findBestMatching(skCtx: SkContext, matchMode = ESkMatchMode.all, contextCard = '1') {
		switch (matchMode) {
		case ESkMatchMode.all: {
			skCtx.forEachChild(ch => {
				if (ch.nodeType === ENodeType.text) {
					skCtx.bindNodeToRule(ch, this);
				} else {
					skCtx.bindNodeToRule(ch, this.getUnknownNodeRule(skCtx.skNode, ch));
				}
			});
			skCtx.forEachAttr(a => {
				skCtx.bindAttrToRule(a, this.getUnknownAttRule(skCtx.skNode, a.nodeName));
			});
			break;
		}
		case ESkMatchMode.ordered:
			skCtx.forEachBindableChild(ch => {
				if (ch.nodeType === ENodeType.text) {
					skCtx.bindNodeToRule(ch, this);
				} else return true; //on stop le parcours des fils.
			});
		}
	}

	execActions(skCtx: SkContext, contextCard: ECard = '1') {
		const opts = skCtx.execOptions;
		if (opts.list1InsertableNodesAt !== undefined) {
			if (opts.insertableNodes1.indexOf(this) < 0) opts.insertableNodes1.push(this);
			if (opts.list2InsertableNodesAt >= 0) {
				if (opts.insertableNodes2.indexOf(this) < 0) opts.insertableNodes2.push(this);
			}
		}

		let addFindOffsetAfter;
		if (opts.findOffsetNodeType && this.structMatch(opts.findOffsetNodeType)) {
			switch (opts.findOffsetIfExistAndCardN) {
			case "reject":
				//opts.findOffset = this.isMatching(skCtx) ? -1 : 0; NON pour ne pas évaluer la validité de value / data.
				opts.findOffset = skCtx.currentChild instanceof Text ? -1 : 0;
				break;
			case "start":
				if (!('findOffset' in opts)) opts.findOffset = skCtx.currentOffset;
				break;
			case "end":
				addFindOffsetAfter = true;
			}
		}

		if (opts.importContent) {
			const impCt = opts.importContent;
			let isTxt = false;
			if (impCt instanceof SkRule) isTxt = impCt.structType === ENodeType.text;
			else if (impCt instanceof Node) isTxt = impCt.nodeType === ENodeType.text;
			else isTxt = typeof impCt === "string";
			if (isTxt) {
				if ('pasteOffsetMin' in opts) {
					opts.pastePos = EPastePos.anywhere;
				} else {
					opts.importPos = {insertOffsetMin: 0, insertOffsetMax: skCtx.node.childNodes.length}
				}
			}
		}

		if (skCtx.currentChild instanceof Text) { //NON this.isMatching(skCtx) : pour ne pas évaluer la validité de value / data.
			const first = skCtx.currentChild;
			if (opts.genAnnots) this._genAnnots(skCtx, contextCard);
			if (opts.autoNormXml) {
				do {
					//note : les commentaires bloquent le merge, donc on répète après chaque commentaire
					let toMerge: string[];
					while (skCtx.currentChild.nextSibling instanceof Text) {
						//on fusionne les noeuds textes consécutifs
						skCtx.currentChild = skCtx.currentChild.nextSibling;
						if (!toMerge) toMerge = [skCtx.currentChild.nodeValue];
						else toMerge.push(skCtx.currentChild.nodeValue);
					}
					skCtx.currentChild = skCtx.currentChild.nextSibling; //on passe au noeud suivant.
					let str;
					if (toMerge) {
						const xaText = XA.from(first);
						str = toMerge.join('');
						if (str) opts.corrections.push(new XmlInsertMsg().init(XA.newBd(xaText).append(first.length).xa, str));
						//note : pas besoin de décaler si suppr avant car les corrections sont initialisés en mode concurrent.
						opts.corrections.push(new XmlDeleteMsg().init(XA.incrAtDepth(xaText, -1, 1), toMerge.length));
					} else {
						if (opts.autoNormChars) skCtx.skNode.rule.skMeta.normChars(first, opts.corrections, false, null);
					}
				} while (skCtx.currentChild instanceof Text); //répète en cas de coupure par un comment.
			} else {
				if (opts.autoNormChars) skCtx.skNode.rule.skMeta.normChars(skCtx.currentChild, opts.corrections);
				skCtx.currentChild = skCtx.currentChild.nextSibling;
				while (skCtx.currentChild instanceof Text) {
					if (opts.autoNormChars) skCtx.skNode.rule.skMeta.normChars(skCtx.currentChild, opts.corrections);
					skCtx.currentChild = skCtx.currentChild.nextSibling;
				}
			}
		} else {
			if (opts.genAnnots) this.validStr(null, contextCard, skCtx.node as Element, skCtx);
		}

		if (addFindOffsetAfter) opts.findOffset = skCtx.currentOffsetMax;
	}

	/** Validation des noeuds texte de type data / value / list (supprimé par surcharge pour le type text). */
	protected _genAnnots(skCtx: SkContext, contextCard: ECard) {
		//Text.wholeText ne traverse pas les comments
		//on repart du container.
		this.validStr(skCtx.node.textContent, contextCard, skCtx.node as Element, skCtx);
	}

	matchStr(str: string, contextCard: ECard): boolean {return true}

	//matchStrList(strs: string[], contextCard: OCard): boolean {return true}

	validStr(str: string, contextCard: ECard, anchor: Element | Attr, skCtx: SkContext): void {
	}

	// validStrList(strs: string[], contextCard: OCard, anchor: Node | Attr, skCtx: SkContext): void {
	// }

	xAppendInsertables(skCtx: SkContext, listNodes: ISkStructDef[], listAttrs?: ISkStructDef[]) {
		if (listNodes?.indexOf(this) < 0) listNodes.push(this);
	}
}


/** Rule pour un noeud texte générique. */
export class SkRuleText extends SkRuleStr {

	get card(): ECard {return '*'}

	// @ts-ignore
	get structLabel() {return "Texte"}

	protected _genAnnots(skCtx: SkContext, contextCard: ECard) {}; //optimisation.

	indexPatterns(rulesIndex: SkRule[]) {
	}

	writeTo(o: any, mode: ESerialMode) {
	}

	readFrom(o: any, mode: ESerialMode): this {
		return SkRuleText.DEFAULT as this;
	}

	static readonly type: string = "#";

	static readonly DEFAULT = Object.freeze(new SkRuleText().linkRules(null));
}

SkRule.registry.register(SkRuleText);

/** Rule de donnée typée pour un noeud texte et pour valider le contenu d'un attribut (cf SkRuleAttr.valueCheck). */
export abstract class SkRuleData extends SkRuleStr {
	dataParams: Dict<string>;

	protected _card: ECard;

	get card(): ECard {return this._card || '1'}

	set card(card: ECard) {
		this._card = card;
	}

	initParams(wsType: string, dataParams: Dict<string>): this {
		this.dataParams = dataParams;
		return this;
	}

	/**
	 * Pour les data et value, on évalue le contenu du texte pour le matching.
	 * Nécessaire pour un choice entre data ou values.
	 */
	isMatching(skCtx: SkContext): boolean {
		const node = skCtx.currentChild;
		if (!node || !(node instanceof Text)) return false;
		return this.evalStr(skCtx.node.textContent);
	}

	matchStr(str: string, contextCard: ECard): boolean {
		if (!str && CARD.isOptionnal(CARD.combineCard(contextCard, this.card))) return true;
		return this.evalStr(str);
	}

	// matchStrList(strs: string[], contextCard: OCard): boolean {
	// 	let card = CARD.combineCard(contextCard, this.card);
	// 	if (!strs || strs.length === 0) return CARD.isOptionnal(card);
	// 	if (!this.evalStr(strs[0])) return false;
	// 	if (strs.length > 1) {
	// 		if (!CARD.isRepeatable(card)) return false;
	// 		for (let i = 1; i < strs.length; i++) {
	// 			if (!this.evalStr(strs[i])) return false;
	// 		}
	// 	}
	// 	return true;
	// }

	protected abstract evalStr(str: string): boolean;

	validStr(str: string, contextCard: ECard, anchor: Element | Attr, skCtx: SkContext): void {
		if (str == null) {
			if (CARD.isRequired(CARD.combineCard(contextCard, this.card))) skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, "Valeur obligatoire"));
		} else {
			this.annotStr(str, anchor, skCtx);
		}
	}

	protected abstract annotStr(str: string, anchor: Element | Attr, skCtx: SkContext): void;

	// validStrList(strs: string[], contextCard: OCard, anchor: Node | Attr, skCtx: SkContext): void {
	// }

	getDefaultValue(): string {return this.dataParams?.defaultValue || ""}

	writeTo(o: any, mode: ESerialMode) {
		super.writeTo(o, mode);
		o.dp = this.dataParams;
	}

	readFrom(o: any, mode: ESerialMode): this {
		super.readFrom(o, mode);
		this.dataParams = o.dp;
		return this;
	}

	static dataTypes: Dict<Constructor<SkRuleData>> = {};

	/** datatypeLibrary */
	static registerDataTypes(type: string, cst: Constructor<SkRuleData>) {
		SkRuleData.dataTypes[type] = cst;
	}

	static newData(type: string, params: Jsonisable): SkRuleData {
		const Cst = SkRuleData.dataTypes[type];
		if (Cst) return new Cst().initParams(type, params);
		//SkRuleData par défaut : SkRuleDataString
		return new SkRuleDataString();
	}
}

/** https://www.w3.org/TR/xmlschema-2/#string */
export class SkRuleDataString extends SkRuleData {

	protected minLen: number;
	protected maxLen: number;
	protected minLenWarn?: number;
	protected maxLenWarn?: number;
	protected pattern?: RegExp;
	protected patternMsg?: string;
	protected patternWarn?: RegExp;
	protected patternWarnMsg?: string;

	protected evalStr(str: string): boolean {
		this.parseParams();
		if (str.length < this.minLen || str.length > this.maxLen) return false;
		if (this.pattern && !this.pattern.test(str)) return false;
		return true;
	}

	protected annotStr(str: string, anchor: Element | Attr, skCtx: SkContext) {
		this.parseParams();
		if (str.length < this.minLen) {
			if (this.minLen === 1) {
				skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, "Valeur obligatoire"));
			} else {
				skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, `Valeur trop courte, inférieure à ${this.minLen} caractères`));
			}
		} else if (this.minLenWarn > 0 && str.length < this.minLenWarn) {
			skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, `Valeur recommandée supérieur à ${this.minLenWarn} caractères`, EAnnotLevel.warning));
		}
		if (str.length > this.maxLen) {
			skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, `Valeur trop longue, supérieure à ${this.maxLen} caractères`));
		} else if (this.maxLenWarn > 0 && str.length > this.maxLenWarn) {
			skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, `Valeur recommandée inférieur à ${this.maxLenWarn} caractères`, EAnnotLevel.warning));
		}
		if (this.pattern && !this.pattern.test(str))
			skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, this.patternMsg || `Valeur incorrecte (${this.pattern})`));
		else if (this.patternWarn && !this.patternWarn.test(str))
			skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, this.patternWarnMsg || `Valeur non recommandée (${this.pattern})`, EAnnotLevel.warning));
	}


	protected parseParams() {
		if (this.minLen !== undefined) return;
		const params = this.dataParams || {};
		this.minLen = params.minLength ? Number.parseInt(params.minLength, 10) : 0;
		this.maxLen = params.maxLength ? Number.parseInt(params.maxLength, 10) : Number.MAX_SAFE_INTEGER;
		if (params.minLengthWarn) this.minLenWarn = Number.parseInt(params.minLengthWarn, 10);
		if (params.maxLengthWarn) this.maxLenWarn = Number.parseInt(params.maxLengthWarn, 10);
		if (params.pattern) {
			this.pattern = new RegExp(params.pattern);
			if (params.patternMsg) this.patternMsg = params.patternMsg;
		}
		if (params.patternWarn) {
			this.patternWarn = new RegExp(params.patternWarn);
			if (params.patternWarnMsg) this.patternWarnMsg = params.patternWarnMsg;
		}
	}

	static readonly type: string = "#string";
}

SkRuleData.registerDataTypes('string', SkRuleDataString);
SkRule.registry.register(SkRuleDataString);


/** https://www.w3.org/TR/xmlschema-2/#token */
export class SkRuleDataToken extends SkRuleDataString {
	protected evalStr(str: string): boolean {
		return super.evalStr(DOM.txtNormToken(str));
	}

	protected annotStr(str: string, anchor: Element | Attr, skCtx: SkContext) {
		super.annotStr(DOM.txtNormToken(str), anchor, skCtx);
	}

	static readonly type: string = "#token";
}

SkRuleData.registerDataTypes('token', SkRuleDataToken);
SkRule.registry.register(SkRuleDataToken);


/** https://www.w3.org/TR/xmlschema-2/#decimal, https://www.w3.org/TR/xmlschema-2/#integer*/
export class SkRuleDataDecimal extends SkRuleData {

	public wsType: 'integer' | 'int' | 'long' | 'decimal' | 'float';

	protected minIncl: number;
	protected maxIncl: number;
	protected fractionDigits: number;

	initParams(wsType: 'integer' | 'int' | 'long' | 'decimal' | 'float', dataParams: Dict<string>): this {
		this.wsType = wsType;
		this.dataParams = dataParams;
		return this;
	}

	protected evalStr(str: string): boolean {
		return this.valid(str) === true;
	}

	protected valid(str: string): true | 'required' | 'invalid' | 'min' | 'max' | 'frac' {
		if (!str) return 'required';
		this.parseParams();
		let val: number;
		switch (this.wsType) {
		case 'integer':
		case 'long':
		case 'int':
			if (!/^[+-]?\d+$/.test(str)) return 'invalid';
			val = Number.parseInt(str, 0);
			break;
		case 'decimal':
			if (!/^[+-]?((\d+\.?\d*)|(\d*\.\d+))$/.test(str)) return 'invalid';
			val = Number.parseFloat(str);
			const point = str.indexOf('.');
			if (point >= 0 && str.length - point - 1 > this.fractionDigits) return 'frac';
			break;
		case 'float':
			if (!/^[+-]?((\d+\.?\d*)|(\d*\.\d+))([eE]((\d+\.?\d*)|(\d*\.\d+)))?$/.test(str)) return 'invalid';
			val = Number.parseFloat(str);
			break;
		}
		if (val < this.minIncl) return 'min';
		if (val > this.maxIncl) return 'max';
		return true;
	}

	protected annotStr(str: string, anchor: Element | Attr, skCtx: SkContext) {
		switch (this.valid(str)) {
		case true:
			return;
		case 'required':
			skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, "Valeur obligatoire"));
			break;
		case 'invalid':
			let t: string;
			switch (this.wsType) {
			case 'integer':
			case 'long':
			case 'int':
				t = `Valeur numérique entière incorrecte : ${str}`;
				break;
			case 'decimal':
				t = `Valeur numérique décimale incorrecte : ${str}`;
				break;
			case "float":
				t = `Valeur numérique flottante incorrecte : ${str}`;
				break;
			}
			skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, t));
			break;
		case 'min':
			skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, `Valeur trop petite : ${str} < ${this.minIncl}`));
			break;
		case 'max':
			skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, `Valeur trop grande : ${str} > ${this.maxIncl}`));
			break;
		case 'frac':
			const fractionDigits = this.fractionDigits;
			const val = Number.parseFloat(str);
			str = val.toLocaleString("fr");
			skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, `Valeur décimale trop fine (${fractionDigits} décimales maximum) : ${str}`));
			break;
		}
	}

	protected parseParams() {
		if (this.minIncl !== undefined) return;
		const params = this.dataParams || {};
		this.fractionDigits = params.fractionDigits ? Number.parseInt(params.fractionDigits, 10) : Number.POSITIVE_INFINITY;
		//FIXME bornes min / max pour long (https://www.w3.org/TR/xmlschema-2/#long). Faut passer en bigInt : Number.MIN_SAFE_INTEGER est insuffisant pour 'long'.
		this.minIncl = params.minInclusive ? Number.parseInt(params.minInclusive, 10) : this.wsType === 'int' ? -2147483648 : Number.NEGATIVE_INFINITY;
		this.maxIncl = params.maxInclusive ? Number.parseInt(params.maxInclusive, 10) : this.wsType === 'int' ? 2147483647 : Number.POSITIVE_INFINITY;
	}

	static readonly type: string = "#decimal";
}

SkRuleData.registerDataTypes('decimal', SkRuleDataDecimal);
SkRuleData.registerDataTypes('integer', SkRuleDataDecimal);
SkRuleData.registerDataTypes('long', SkRuleDataDecimal);
SkRuleData.registerDataTypes('int', SkRuleDataDecimal);
SkRuleData.registerDataTypes('float', SkRuleDataDecimal);
SkRule.registry.register(SkRuleDataDecimal);


/**
 * https://www.w3.org/TR/xmlschema-2/#date
 * Restrictions par rapport à xmlschema :
 * - années négatives non autorisée.
 * - timezone non autorisée.
 *
 * ATTENTION : aucune validation réelle syntaxique
 */
export class SkRuleDataDate extends SkRuleDataString {
	static datePattern = /^\d{4}-\d{2}-\d{2}$/;

	static validDate(str: string): true | 'required' | 'invalid' | 'non-exist' {
		if (!str) return 'required';
		if (!SkRuleDataDate.datePattern.test(str)) return 'invalid';
		const d = new Date(str);
		if (d.getDate() !== Number.parseInt(str.substring(8, 10))
			|| d.getMonth() + 1 !== Number.parseInt(str.substring(5, 7))
			|| d.getFullYear() !== Number.parseInt(str.substring(0, 4))) return 'non-exist';
		return true;
	}

	getDefaultValue(): string {
		if (this.dataParams?.defaultValue === '#now')
			return new Date().toISOString().split("T")[0];
		return super.getDefaultValue();
	}

	protected evalStr(str: string): boolean {
		return SkRuleDataDate.validDate(str) === true;
	}

	protected annotStr(str: string, anchor: Element | Attr, skCtx: SkContext) {
		switch (SkRuleDataDate.validDate(str)) {
		case true:
			return;
		case 'required':
			skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, "Valeur obligatoire"));
			break;
		case 'invalid':
			skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, `Date au format YYYY-MM-JJ incorrecte : ${str}`));
			break;
		case 'non-exist':
			skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, `Date inexistante dans le calendrier : ${str}`));
			break;
		}
	}

	static readonly type: string = "#date";
}

SkRuleData.registerDataTypes('date', SkRuleDataDate);
SkRule.registry.register(SkRuleDataDate);


/**
 * Correspond à <value type="string|token"/> dans Rng.
 */
export class SkRuleValue extends SkRuleData {
	dataType: 'string' | 'token';
	value: string;

	init(type: string, value: string) {
		if (type === 'token') this.dataType = type;
		this.value = value;
		return this;
	}

	get structName() {return "'" + this.value + "'"}

	getDefaultValue(): string {return this.value}

	protected evalStr(str: string): boolean {
		if (this.dataType !== 'token') return this.value === str;
		return this.value === str || this.value === DOM.txtNormToken(str);
	}

	validStr(str: string, contextCard: ECard, anchor: Element | Attr, skCtx: SkContext): void {
		//On tue le controle générique de str!="" de SkRuleData (pour les autres valeurs typées)
		this.annotStr(str, anchor, skCtx);
	}

	protected annotStr(str: string, anchor: Element | Attr, skCtx: SkContext) {
		if (str == null) str = "";
		if (this.value === str) return;
		if (this.dataType === 'token') {
			str = DOM.txtNormToken(str);
			if (this.value === str) return;
		}
		const val = this.value;
		skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, `Valeur non autorisée ('${val}') : '${str}'`));
	}

	writeTo(o: any, mode: ESerialMode) {
		super.writeTo(o, mode);
		if (this.dataType === 'token') o.t = 'token';
		o.v = this.value;
	}

	readFrom(o: any, mode: ESerialMode): this {
		super.readFrom(o, mode);
		this.dataType = o.t;
		this.value = o.v;
		return this;
	}

	static readonly type: string = "#v";
}

SkRule.registry.register(SkRuleValue);

//
//
// /**
//  * Correspond à <value type="ID"/> dans Rng.
//  */
// export class SkRuleID extends SkRuleData {
//
// 	get structName() {return "#ID"}
//
// 	getDefaultValue(): string {
//
// 		return super.getDefaultValue();
// 	}
//
// 	protected evalStr(str: string): boolean {
// 		return true;
// 	}
//
// 	protected annotStr(str: string, anchor: Element | Attr, skCtx: SkContext) {
// 	}
//
// 	static readonly type: string = "#ID";
// }
//
// SkRule.registry.register(SkRuleID);


export class SkRuleList extends SkRuleStr {

	contentRule: ISkRuleStr;

	protected _card: ECard;

	get card(): ECard {return this._card || '1'}

	set card(card: ECard) {
		this._card = card
	}

	matchStr(str: string, contextCard: ECard): boolean {
		console.trace("TODO SkRuleList.matchStr()");
		return true;
		//return this.contentRule.matchStrList(this.parseValue(str), CARD.combineCard(contextCard, this.card));
	}

	// matchStrList(strs: string[], contextCard: OCard): boolean {
	// 	throw Error("Schema error : List in list not allowed");
	// }

	/** Evalue cette string et publie les annotations d'erreur. */
	validStr(str: string, contextCard: ECard, anchor: Element | Attr, skCtx: SkContext): void {
		console.trace("TODO SkRuleList.validStr()");
		//this.contentRule.validStrList(this.parseValue(str), CARD.combineCard(contextCard, this.card), anchor, skCtx);
	}

	// validStrList(strs: string[], contextCard: OCard, anchor: Node | Attr, skCtx: SkContext): void {
	// 	throw Error("Schema error : List in list not allowed");
	// }

	parseValue(str: string): string[] {return str ? str.trim().split(/\s+/) : null}

	static readonly type: string = "#l";
}

SkRule.registry.register(SkRuleList);


export class SkRuleTextWs extends SkRuleStr {
	static readonly type = "#!";

	static readonly DEFAULT = Object.freeze(new SkRuleTextWs());

	execActionsOnBinding(skCtx: SkContext, node: Node) {
		if (skCtx.execOptions.autoCleanup) skCtx.execOptions.corrections.push(new XmlDeleteMsg().init(XA.from(node), 1));
	}

	readFrom(o: any, mode: ESerialMode): this {
		return SkRuleTextWs.DEFAULT as this;
	}
}

SkRule.registry.register(SkRuleTextWs);


export class SkRuleTextForbidden extends SkRuleStr {
	static readonly type = "#!";

	static readonly DEFAULT = Object.freeze(new SkRuleTextForbidden());

	execActionsOnBinding(skCtx: SkContext, node: Node) {
		if (skCtx.skNode.rule instanceof SkRuleEltUnknown) return; //Aucun traitement si on n'est pas un noeud texte unkonwn root.
		if (skCtx.execOptions.autoCleanup) skCtx.execOptions.corrections.push(new XmlDeleteMsg().init(XA.from(node), 1));
		else if (skCtx.execOptions.genAnnots) skCtx.addAnnot(new SkAnnotTextForbidden().init(node as CharacterData));
	}

	readFrom(o: any, mode: ESerialMode): this {
		return SkRuleTextForbidden.DEFAULT as this;
	}
}

SkRule.registry.register(SkRuleTextForbidden);


/**
 * Rule pour un noeud comment. Jamais utilisé pour valider un schéma,
 * mais nécessaire pour configurer le comportement des commentaires
 * notamment pour le ISkMetaNode (SkMComment).
 */
export class SkRuleComment extends SkRuleNode {

	// @ts-ignore
	get structType() {return ENodeType.comment}

	createContent(childrenToAppend?: IJmlSet, attsToAppend?: IJmlObj): void | string {
		childrenToAppend.push({'': JML.COMMENT});
	}

	structMatch(nodeType: ENodeType, nodeName?: string): boolean {return nodeType === ENodeType.comment}

	// @ts-ignore
	get contentRule() {return SkRuleEmpty.SINGLETON}

	// @ts-ignore
	get matcher(): SkMatcherNode {return SkMatcherComment.SINGLETON}

	get card(): ECard {return '*'}

	indexPatterns(rulesIndex: SkRule[]) {
		if (this === SkRuleComment.DEFAULT) return;
		super.indexPatterns(rulesIndex);
	}

	writeTo(o: any, mode: ESerialMode) {
		if (this === SkRuleComment.DEFAULT) return;
		super.writeTo(o, mode);
		//on let o.chk à null pour différencier de SkRuleComment.DEFAULT.
		o.chk = null;
	}

	readFrom(o: any, mode: ESerialMode): this {
		if (o.chk === undefined) return SkRuleComment.DEFAULT as this;
		super.readFrom(o, mode);
		return this;
	}

	static readonly type: string = "!";

	static readonly DEFAULT = new SkRuleComment();
}

SkRule.registry.register(SkRuleComment);

/**
 * Règle correspondant à un attribut.
 */
export class SkRuleAttr extends SkRule {
	static readonly type: string = "@";

	matcher: SkMatcherAttr;

	mandat: boolean;

	/** Entrée d'aide. */
	helpId?: helpId

	/** Contrôle de la valeur de l'attribut. */
	valueCheck: ISkRuleStr;

	/** Ref temporaire au contenu avant linkage pouvant pointer la contentRule par son name. */
	tmpValChk: string /* pour deserialisation : */ | number | ISkRuleStr;

	init(matcher: SkMatcherAttr, mandat: boolean = true, valueCheck?: ISkRuleStr) {
		this.matcher = matcher;
		this.mandat = mandat;
		this.valueCheck = valueCheck;
		return this;
	}

	initValueCheck(valueCheck: ISkRuleStr): this {
		this.valueCheck = valueCheck;
		return this;
	}

	initValueCheckByName(valueCheck: string): this {
		this.tmpValChk = valueCheck;
		return this;
	}

	// @ts-ignore
	get structName() {return this.matcher.techName}

	structMatch(nodeType: ENodeType, nodeName?: string): boolean {return nodeType === ENodeType.attribute && (nodeName == null || this.matcher.matchAttr(nodeName))}

	// @ts-ignore
	get structType() {return this.matcher.isFuzzy() ? EFuzzyType.attributes : ENodeType.attribute}

	createContent(childrenToAppend?: IJmlSet, attsToAppend?: IJmlObj): void | string {
		if (this.matcher.isFuzzy()) return this.matcher.startName;
		attsToAppend[this.matcher.startName] = this.valueCheck?.getDefaultValue() || "";
	}

	linkRules(map: ISkPatternResolver): SkRule {
		if (this.tmpValChk != null) {
			this.valueCheck = map.resolveRule(this.tmpValChk) as ISkRuleStr;
			this.tmpValChk = undefined;
		}
		if (this.valueCheck) this.valueCheck.linkRules(map);
		return this;
	}

	get card(): ECard {return this.mandat ? '1' : '?'}

	set card(card: ECard) {
		this.mandat = CARD.isRequired(card);
	}

	findRuleAttrFor(attr: string) {
		return this.matcher.matchAttr(attr) ? this : null
	}

	isMatching(skCtx: SkContext) {
		let match = false;
		skCtx.forEachBindableAttr(a => {
			if (this.matcher.matchAttr(a.nodeName)) {
				match = true;
				return true;
			}
		});
		return match;
	}

	findBestMatching(skCtx: SkContext, matchMode = ESkMatchMode.all, contextCard: ECard) {
		let found: Attr;
		if (matchMode === ESkMatchMode.all) {
			skCtx.forEachChild(ch => {
				skCtx.bindNodeToRule(ch, this.getUnknownNodeRule(skCtx.skNode, ch));
			});
			skCtx.forEachAttr((a: Attr) => {
				if (this.matcher.matchAttr(a.nodeName)) {
					skCtx.bindAttrToRule(a, this);
					found = a;
				} else {
					skCtx.bindAttrToRule(a, this.getUnknownAttRule(skCtx.skNode, a.nodeName));
				}
			});
		} else {
			skCtx.forEachBindableAttr((a: Attr) => {
				if (this.matcher.matchAttr(a.nodeName)) {
					skCtx.bindAttrToRule(a, this);
					found = a;
				}
			});
		}
		skCtx.pushRuleState(found);
	}

	execActions(skCtx: SkContext, contextCard: ECard = '1') {
		const opts = skCtx.execOptions;
		const attr = skCtx.shiftRuleState() as Attr;
		if (!attr) {
			if (this.mandat) {
				if (opts.autoComplete) {
					opts.corrections.push(new XmlStrMsg().init(XA.append(XA.from(skCtx.node), this.matcher.startName), this.valueCheck?.getDefaultValue() || ""));
				} else if (opts.genAnnots) {
					skCtx.addAnnot(new SkAnnotAttrMissing().init(skCtx.node as Element, this));
				}
			}
			if (opts.insertableAttrs) this.xAppendInsertables(skCtx, null, opts.insertableAttrs);
		} else {
			if (opts.insertableAttrs && this.matcher.isFuzzy()) this.xAppendInsertables(skCtx, null, opts.insertableAttrs);
			if (this.valueCheck && opts.genAnnots) this.valueCheck.validStr(attr.nodeValue, '1', attr, skCtx);
			if (opts.autoNormChars && this.skMeta) this.skMeta.normChars(attr, opts.corrections);
		}
		if (opts.findOffsetNodeType && this.structMatch(opts.findOffsetNodeType, opts.findOffsetNodeName)) {
			if (!opts.findOffsetNodeName) {
				//Attr libre demandé (anyName) et autorisé ici.
				opts.findOffset = 0;
			} else {
				//opts.findOffset = attr ? -1 : 0; NON : cet attr a pu être capté dans un autre groupe et donc attr=null, mais impossible si existe déjà dans l'elt.
				opts.findOffset = (skCtx.node as Element).hasAttribute(opts.findOffsetNodeName) ? -1 : 0;
			}
		}
	}

	/**
	 * Appelé suite à l'association "virtuelle" entre un attribut et sa règle ("virtuelle" car les attributs ne sont pas
	 * physiquement bindés via un SkNode).
	 */
	execActionsOnBinding(skCtx: SkContext, attr: Attr) {
	}

	xAppendInsertables(skCtx: SkContext, listNodes: ISkStructDef[], listAttrs?: ISkStructDef[]) {
		if (listAttrs && listAttrs.indexOf(this) < 0 && this.skMeta.isInsertable(skCtx.skNode, this)) {
			const elt = skCtx.node;
			if (!(elt instanceof Element) || this.matcher.isFuzzy() || !elt.hasAttribute(this.matcher.startName)) listAttrs.push(this);
		}
	}

	indexPatterns(rulesIndex: SkRule[]) {
		if (Object.isFrozen(this)) return;
		if (this.id === undefined) {
			this.id = rulesIndex.length;
			rulesIndex.push(this);
			if (this.valueCheck) this.valueCheck.indexPatterns(rulesIndex);
		}
	}

	writeTo(o: any, mode: ESerialMode) {
		super.writeTo(o, mode);
		o.matcher = Serializable.serialSerializable(this.matcher, mode);
		if (this.valueCheck != null) o.chk = SkRule.serialRefRule(this.valueCheck, mode);
		if (this.mandat) o.mandat = true;
	}

	readFrom(o: any, mode: ESerialMode): this {
		super.readFrom(o, mode);
		this.matcher = Serializable.deserialSerializable(o.matcher, mode, SkMatcherAttr.registry);
		if (o.chk) this.tmpValChk = SkRule.deserialRefRule(o.chk, mode) as ISkRuleStr | number;
		this.mandat = o.mandat === true;
		return this;
	}
}

SkRule.registry.register(SkRuleAttr);

export class SkRuleAttrUnknown extends SkRuleAttr {
	static readonly type = "@?";

	initUnknown(matcher: SkMatcherAttr = SkMatcherAnyAttr.SINGLETON, skMeta = SKMETALIB.getMetaNode('@')): this {
		this.matcher = matcher;
		this.skMeta = skMeta;
		return this;
	}

	findBestMatching(skCtx: SkContext, matchMode: ESkMatchMode, contextCard: ECard) {
		//Les rules pour les attr inconnus ne sont pas intégrés dans le jeu de règles de SkRuleNode.contentRule
		//Ne sera donc jamais appelée
		console.log("findBestMatching::SkRuleAttrUnknown:", skCtx);
	}

	execActions(skCtx: SkContext, contextCard: ECard) {
	}

	execActionsOnBinding(skCtx: SkContext, attr: Attr) {
		if (skCtx.skNode.rule instanceof SkRuleEltUnknown) return; //Aucun traitement si on n'est pas un attr unkonwn root.
		if (skCtx.execOptions.autoCleanup) skCtx.execOptions.corrections.push(new XmlStrMsg().init(XA.from(attr), null));
		else if (skCtx.execOptions.genAnnots) skCtx.addAnnot(new SkAnnotAttrUnknown().init(attr));
	}

	writeTo(o: any, mode: ESerialMode) {
		if (this === SkRuleAttrUnknown.DEFAULT) return;
		super.writeTo(o, mode);
		if (this.matcher != SkMatcherAnyAttr.SINGLETON) o.matcher = Serializable.serialSerializable(this.matcher, mode);
		if (this.valueCheck != null) o.chk = SkRule.serialRefRule(this.valueCheck, mode);
		o.mandat = this.mandat;
	}

	readFrom(o: any, mode: ESerialMode): this {
		if (o.mandat === undefined) return SkRuleAttrUnknown.DEFAULT as this;
		super.readFrom(o, mode);
		if (o.matcher) this.matcher = Serializable.deserialSerializable(o.matcher, mode, SkMatcherAttr.registry);
		if (o.chk) this.tmpValChk = SkRule.deserialRefRule(o.chk, mode) as ISkRuleStr | number;
		this.mandat = o.mandat === true;
		return this;
	}

	static readonly DEFAULT = new SkRuleAttrUnknown();
}

SkRule.registry.register(SkRuleAttrUnknown);


export class SkRuleChoice extends SkRuleDirective implements ISkRuleStr {
	static readonly type = "|";

	/** */
	static readonly ANY = new SkRuleChoice();

	// @ts-ignore
	get structName() {
		//if (this.uniqueName != null) return this.uniqueName;
		if (!this._builtName) {
			const buf = ["("];
			this.subRules.forEach((r, i) => {
				const card = CARD.combineCard(this.overSubCard(i, '1'), r.card);
				if (card !== '1') {
					buf.push(r.structName, card, "|");
				} else {
					buf.push(r.structName, "|");
				}
			});
			buf[buf.length - 1] = ")";
			this._builtName = buf.join('');
		}
		return this._builtName;
	}

	// @ts-ignore
	get structType() {return EDirectiveType.choice}


	autoCreate?: "first";

	initAutoCreate(v: string | null): this {
		this.autoCreate = v === "first" ? "first" : undefined;
		return this;
	}

	/** Pour valeur par défaut d'une énum. */
	defaultValue?: string

	initDefaultValue(d?: string): this {
		if (d) this.defaultValue = d;
		return this;
	}

	findBestMatching(skCtx: SkContext, matchMode = ESkMatchMode.all, contextCard: ECard = '1') {
		const card = CARD.combineCard(contextCard, this.card);
		const stateIdx = skCtx.reserveRuleState();
		let found = this.xFindBestMatchingSub(skCtx, card);
		if (matchMode === ESkMatchMode.all) {
			//Directive racine, tant qu'il reste des nodes inconnus, on les exclut un à un et on recommence.
			while (skCtx.currentChild) {
				skCtx.bindUnknownNodeToRuleAndReset(skCtx.currentChild, this.getUnknownNodeRule(skCtx.skNode, skCtx.currentChild), stateIdx);
				found = this.xFindBestMatchingSub(skCtx, card);
			}
			skCtx.forEachBindableAttr(a => {
				skCtx.bindAttrToRule(a, this.getUnknownAttRule(skCtx.skNode, a.nodeName));
			});
		}
		skCtx.setRuleState(stateIdx, found);
	}

	xFindBestMatchingSub(skCtx: SkContext, card: ECard) {
		let found: (SkRule | Node | number)[];
		repeat: do {
			for (let i = 0; i < this.subRules.length; i++) {
				const rule = this.subRules[i];
				if (rule.isMatching(skCtx)) {
					if (!found) found = [rule, skCtx.currentChild];
					else found.push(rule, skCtx.currentChild);
					rule.findBestMatching(skCtx, ESkMatchMode.ordered, this.overSubCard(i, card));
					found.push(skCtx.currentOffsetMax);
					continue repeat;
				}
			}
			break;
		} while (CARD.isRepeatable(card));
		return found;
	}

	isMatching(skCtx: SkContext) {
		for (let i = 0; i < this.subRules.length; i++) {
			if (this.subRules[i].isMatching(skCtx)) return true;
		}
		return false;
	}

	execActions(skCtx: SkContext, contextCard: ECard = '1') {
		const card = CARD.combineCard(contextCard, this.card);
		const found = skCtx.shiftRuleState() as Array<SkRule | Node | number>;
		const opts = skCtx.execOptions;
		const list1InsertableNodesAt = opts.list1InsertableNodesAt;
		const insertableAttrs = opts.insertableAttrs;
		const importContent = opts.importContent;
		let addFindOffsetAfter;
		if (found == null || CARD.isRepeatable(card)) {
			if (list1InsertableNodesAt !== undefined) {
				const offsetMin = skCtx.currentOffsetMin;
				const offsetMax = found ? (found[found.length - 1] as number) : skCtx.currentOffsetMax;
				if (list1InsertableNodesAt === -1 || (offsetMin <= list1InsertableNodesAt && offsetMax >= list1InsertableNodesAt)) {
					this.xAppendInsertables(skCtx, opts.insertableNodes1);
				}
				const insPoint2 = opts.list2InsertableNodesAt;
				if (insPoint2 >= 0 && offsetMin <= insPoint2 && offsetMax >= insPoint2) {
					this.xAppendInsertables(skCtx, opts.insertableNodes2);
				}
				//On kill l'insertion des noeuds insérables.
				opts.list1InsertableNodesAt = undefined;
			}
			if (insertableAttrs) {
				this.xAppendInsertables(skCtx, null, insertableAttrs);
				//On kill l'insertion des noeuds insérables.
				opts.insertableAttrs = null;
			}
			if (opts.findOffsetNodeType && this.structMatch(opts.findOffsetNodeType, opts.findOffsetNodeName)) {
				switch (CARD.isRepeatable(card) ? opts.findOffsetIfExistAndCardN : 'reject') {
				case "reject":
					opts.findOffset = this.isInFound(found, opts.findOffsetNodeType, opts.findOffsetNodeName) ? -1 : skCtx.currentOffset;
					break;
				case "start":
					if (!('findOffset' in opts)) opts.findOffset = skCtx.currentOffset;
					break;
				case "end":
					addFindOffsetAfter = true;
				}
			}
		}
		if (importContent) {
			const subCard = importContent instanceof SkRule ? this.getRealCardSubRule(importContent) :
				importContent instanceof Node ? this.getRealCardSubNode(importContent.nodeType, importContent.nodeName) :
					this.getRealCardSubNode(JML.jmlNode2nodeType(importContent), JML.jmlNode2name(importContent));
			if (subCard !== undefined) {
				//importContent est bien autorisé ici.
				if ('pasteOffsetMin' in opts) {
					if (opts.pasteOffsetMin >= skCtx.currentOffsetMin && opts.pasteOffsetMax <= (found ? (found[found.length - 1] as number) : skCtx.getOffsetMaxAfterCurrent(0))) {
						//A Affiner...
						if (CARD.isRepeatable(subCard)) {
							opts.pastePos = EPastePos.anywhere;
						} else {
							opts.pastePos = EPastePos.replace;
						}
					}
				} else {
					if (CARD.isRepeatable(subCard)) {
						if (found) {
							const maxOffset = found[found.length - 1] as number;
							opts.importPos = {insertOffsetMin: skCtx.currentOffsetMin, insertOffsetMax: maxOffset, replaceChildren: [skCtx.currentOffset]};
							let n = skCtx.currentChild;
							for (let i = skCtx.currentOffset + 1; i < maxOffset; i++) {
								n = n.nextSibling;
								if (n.nodeType === ENodeType.element) opts.importPos.replaceChildren.push(i);
							}
						} else {
							opts.importPos = {insertOffsetMin: skCtx.currentOffsetMin, insertOffsetMax: skCtx.currentOffsetMax};
						}
					} else if (found) {
						opts.importPos = {replaceChildren: [skCtx.currentOffset]};
					} else {
						opts.importPos = {insertOffsetMin: skCtx.currentOffset, insertOffsetMax: skCtx.currentOffset};
					}
				}
			}
			//On kill l'eval de l'import de content pour les subRules
			opts.importContent = null;
		}
		if (found == null) {
			if (CARD.isRequired(card)) {
				let genAn = opts.genAnnots;
				if (opts.autoComplete) {
					//console.log("AutoComplete....");
					if (!this.isStrChoice()) {
						//Si création par défaut forcé
						const content: IJmlSet = [];
						this.createContent(content, {});
						//XXX création des attributs ?
						if (content.length > 0) {
							opts.corrections.push(new XmlInsertMsg().init(XA.append(XA.from(skCtx.node), skCtx.currentOffset), content));
							genAn = false;
						}
					}
				}
				if (genAn) {
					let i: number;
					if (skCtx.newAnnots && this.isStrChoice() && (i = skCtx.newAnnots.findIndex((e) => e instanceof SkAnnotTextForbidden)) >= 0) {
						//On est un choice de string (data dans un texte) et on a détecté un noeud texte interdit.
						//On remplace le couple d'annot "texte interdit" + "texte manquant", par une annot de type WrongValue
						skCtx.newAnnots[i] = new SkAnnotWrongValue().init(skCtx.node as Element, "Valeur incorrecte");
					} else {
						skCtx.addAnnot(new SkAnnotMissing().init(SkAnnotMissing.TYPE_choiceMissing, skCtx.node, skCtx.currentOffset, this));
					}
				}
			}
		} else {
			for (let i = 0; i < found.length; i = i + 3) {
				skCtx.currentChild = found[i + 1] as Node;
				(found[i] as SkRule).execActions(skCtx, card);
			}
		}
		if (addFindOffsetAfter) opts.findOffset = skCtx.currentOffsetMax;
		//On restaure le kill de la construction des noeuds insérables.
		if (list1InsertableNodesAt !== undefined) opts.list1InsertableNodesAt = list1InsertableNodesAt;
		if (insertableAttrs) opts.insertableAttrs = insertableAttrs;
		if (importContent) opts.importContent = importContent;
	}

	isInFound(found: Array<SkRule | Node | number>, nodeType: ENodeType, nodeName: string): boolean {
		if (!found) return false;
		for (let i = 0; i < found.length; i = i + 3) {
			if ((found[i] as SkRule).structMatch(nodeType, nodeName)) return true;
		}
		return false;
	}

	createContent(childrenToAppend?: IJmlSet, attsToAppend?: IJmlObj, guardStack?: SkRuleElt[]): void | string {
		if (this.autoCreate === "first" && this.subRules.length > 0) {
			const lenCh = childrenToAppend.length;
			const lenAtts = Object.getOwnPropertyNames(attsToAppend).length;
			for (let r of this.subRules) {
				//On atteint le 1er creatable (noAutoComplete != true), ie on saute les BlackHole par exemple
				r.createContent(childrenToAppend, attsToAppend, guardStack);
				if (lenCh !== childrenToAppend.length || lenAtts !== Object.getOwnPropertyNames(attsToAppend).length) break; //Un ajout trouvé, on arrête
			}
		} else if (this.subRules.length === 1) {
			const r = this.subRules[0];
			//On ajoute le 1er élement obligatoire du choice.
			if (CARD.isRequired(CARD.combineCard(this.overSubCard(0, '1'), r.card))) {
				r.createContent(childrenToAppend, attsToAppend, guardStack);
			}
		}
	}

	getDefaultValue(): string {return this.defaultValue || ""}

	xAppendInsertables(skCtx: SkContext, listNodes: ISkStructDef[], listAttrs?: ISkStructDef[]) {
		for (let i = 0; i < this.subRules.length; i++) {
			this.subRules[i].xAppendInsertables(skCtx, listNodes, listAttrs);
			// let rule = this.subRules[i];
			// if (rule instanceof SkRuleElt) {
			// 	listNodes.push(rule);
			// } else if (rule instanceof SkRuleAttr) {
			// 	if (listAttrs) listAttrs.push(rule);
			// } else if (rule instanceof SkRuleChoice) {
			// 	rule.xAppendInsertables(listNodes, listAttrs);
			// }
		}
	}

	isStrChoice() {
		if (this._isStrChoice === undefined) this._isStrChoice = this.findRule((r) => !isSkRuleStr(r)) == null;
		return this._isStrChoice;
	}

	protected _isStrChoice: boolean;

	matchStr(str: string, contextCard: ECard): boolean {
		const card = CARD.combineCard(contextCard, this.card);
		for (let i = 0; i < this.subRules.length; i++) {
			if ((this.subRules[i] as ISkRuleStr).matchStr(str, this.overSubCard(i, card))) return true;
		}
		return false;
	}

	validStr(str: string, contextCard: ECard, anchor: Element | Attr, skCtx: SkContext): void {
		if (!this.matchStr(str, contextCard)) {
			if (!str) {
				//noeud texte manquant ou == "", ou attr == "" interdit.
				skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, `Valeur obligatoire`));
			} else {
				skCtx.addAnnot(new SkAnnotWrongValue().init(anchor, this.structLabel ? `Valeur incorrecte (${this.structLabel})` : `Valeur incorrecte`));
			}
		}
	}

	freezeRule() {
		this.isStrChoice(); //compute _isStrChoice
		Object.freeze(this);
	}
}

SkRule.registry.register(SkRuleChoice);

export class SkRuleUGroup extends SkRuleDirective {
	static readonly type = "&";

	// @ts-ignore
	get structName() {
		//if (this.uniqueName != null) return this.uniqueName;
		if (!this._builtName) {
			const buf = ["("];
			this.subRules.forEach((r, i) => {
				const card = this.overSubCard(i, '1');
				if (card !== '1') {
					buf.push(r.structName, card, "&");
				} else {
					buf.push(r.structName, "&");
				}
			});
			buf[buf.length - 1] = ")";
			this._builtName = buf.join('');
		}
		return this._builtName;
	}

	// @ts-ignore
	get structType() {return EDirectiveType.unorderedGroup}

	findBestMatching(skCtx: SkContext, matchMode = ESkMatchMode.all, contextCard: ECard = '1') {
		const card = CARD.combineCard(contextCard, this.card);
		const stateIdx = skCtx.reserveRuleState();
		let found = this.xFindBestMatchingSub(skCtx, card);
		if (matchMode === ESkMatchMode.all) {
			//Directive racine, tant qu'il reste des nodes inconnus, on les exclut un à un et on recommence.
			while (skCtx.currentChild) {
				skCtx.bindUnknownNodeToRuleAndReset(skCtx.currentChild, this.getUnknownNodeRule(skCtx.skNode, skCtx.currentChild), stateIdx);
				found = this.xFindBestMatchingSub(skCtx, card);
			}
			skCtx.forEachBindableAttr(a => {
				skCtx.bindAttrToRule(a, this.getUnknownAttRule(skCtx.skNode, a.nodeName));
			});
		}
		skCtx.setRuleState(stateIdx, found);
	}

	xFindBestMatchingSub(skCtx: SkContext, card: ECard) {
		let found: (SkRule | Node)[];
		if (CARD.isRequired(card) || this.isMatching(skCtx)) {
			//clone la liste des rules pour limiter chaque rule à une seule utilisation.
			const countMatch: number[] = [];
			eachNode: while (skCtx.currentChild) {
				for (let i = 0; i < this.subRules.length; i++) {
					const rule = this.subRules[i];
					const count = countMatch[i] || 0;
					const ruleCard = this.overSubCard(i, card);
					if (count == 0 || CARD.isRepeatable(CARD.combineCard(ruleCard, rule.card))) {
						if (rule.isMatching(skCtx)) {
							countMatch[i] = count + 1;
							if (!found) found = [rule, skCtx.currentChild];
							else found.push(rule, skCtx.currentChild);
							rule.findBestMatching(skCtx, ESkMatchMode.ordered, ruleCard);
							continue eachNode;
						}
					}
				}
				//toutes les rules de l'unorderedGroup ont été passé en revue, on ne progresse plus.
				break;
			}
			if (found && CARD.isRequired(card)) {
				//au moins élément a matché, on mémorise ceux qui n'ont pas matché (gestion du missing).
				for (let i = 0; i < this.subRules.length; i++) {
					//on mémorise les rules qui seront flaguées manquantes
					const rule = this.subRules[i];
					const ruleCard = this.overSubCard(i, card);
					if (!countMatch[i] && CARD.isRequired(CARD.combineCard(ruleCard, rule.card))) {
						found.push(rule, skCtx.currentChild);
						rule.findBestMatching(skCtx, ESkMatchMode.ordered, ruleCard);
					}
				}
			}
		}
		if (CARD.isRepeatable(card)) {// + ou *
			eachNode: while (skCtx.currentChild) {
				for (let i = 0; i < this.subRules.length; i++) {
					const rule = this.subRules[i];
					if (rule.isMatching(skCtx)) {
						if (!found) found = [rule, skCtx.currentChild];
						else found.push(rule, skCtx.currentChild);
						rule.findBestMatching(skCtx, ESkMatchMode.ordered, this.overSubCard(i, card));
						continue eachNode;
					}
				}
				break;
			}
		}
		return found;
	}

	/**
	 * On évalue si le noeud courant du contexte match une des rules de l'unorderedGroup.
	 */
	isMatching(skCtx: SkContext) {
		if (!skCtx.currentChild) return false;
		for (let i = 0; i < this.subRules.length; i++) {
			if (this.subRules[i].isMatching(skCtx)) return true;
		}
		return false;
	}

	execActions(skCtx: SkContext, contextCard: ECard = '1') {
		const card = CARD.combineCard(contextCard, this.card);
		const opts = skCtx.execOptions;
		const found = skCtx.shiftRuleState() as Array<SkRule | Node>;
		if (opts.list1InsertableNodesAt !== undefined) {
			console.log("TODO listInsertableNodes for SkRuleUGroup");
		}
		let addFindOffsetAfter;
		const importContent = opts.importContent;
		if (opts.findOffsetNodeType && this.structMatch(opts.findOffsetNodeType, opts.findOffsetNodeName)) {
			const structFound = this.isInFound(found, opts.findOffsetNodeType, opts.findOffsetNodeName);
			if (!structFound || CARD.isRepeatable(card)) {
				switch (CARD.isRepeatable(card) ? opts.findOffsetIfExistAndCardN : 'reject') {
				case "reject":
					opts.findOffset = structFound ? -1 : skCtx.currentOffset;
					break;
				case "start":
					if (!('findOffset' in opts)) opts.findOffset = skCtx.currentOffset;
					break;
				case "end":
					addFindOffsetAfter = true;
				}
			}
		}
		if (importContent) {
			const subCard = importContent instanceof SkRule ? this.getRealCardSubRule(importContent) :
				importContent instanceof Node ? this.getRealCardSubNode(importContent.nodeType, importContent.nodeName) :
					this.getRealCardSubNode(JML.jmlNode2nodeType(importContent), JML.jmlNode2name(importContent));
			if (subCard !== undefined) {
				//importContent est bien autorisé ici.
				if ('pasteOffsetMin' in opts) {
					if (opts.pasteOffsetMin >= skCtx.currentOffsetMin && opts.pasteOffsetMax <= skCtx.getOffsetMaxAfterCurrent(found ? found.length / 2 : 0)) {
						//A Affiner...
						if (CARD.isRepeatable(card)) {
							opts.pastePos = EPastePos.anywhere;
						} else {
							console.trace("TODO Si importContent isInFound, 0 ou 'replace' (si skCtx.currentChild est la même Rule que importContent) Sinon EPastePos.before | EPastePos.replace | EPastePos.after.");
							//opts.pastePos = EPastePos.replace;
						}
					}
					//On kill l'eval de limport de content pour les subRules
					opts.importContent = null;
				} else {
					if (CARD.isRepeatable(card)) {
						opts.importPos = {insertOffsetMin: skCtx.currentOffsetMin, insertOffsetMax: skCtx.getOffsetMaxAfterCurrent(found ? found.length / 2 : 0)};
					} else if (found) {
						opts.importPos = {replaceChildren: [skCtx.currentOffset]};
					} else {
						opts.importPos = {insertOffsetMin: skCtx.currentOffset, insertOffsetMax: skCtx.currentOffset};
					}
				}
			}
		}
		if (found == null) {
			if (opts.genAnnots && CARD.isRequired(card)) {
				skCtx.addAnnot(new SkAnnotMissing().init(SkAnnotMissing.TYPE_ugroupMissing, skCtx.node, skCtx.currentOffset, this));
			}
		} else {
			for (let i = 0; i < found.length; i = i + 2) {
				skCtx.currentChild = found[i + 1] as Node;
				(found[i] as SkRule).execActions(skCtx, card);
			}
		}
		if (addFindOffsetAfter) opts.findOffset = skCtx.currentOffsetMax;
		if (importContent) opts.importContent = importContent;
	}

	isInFound(found: Array<SkRule | Node>, nodeType: ENodeType, nodeName: string): boolean {
		if (!found) return false;
		for (let i = 0; i < found.length; i = i + 2) {
			if ((found[i] as SkRule).structMatch(nodeType, nodeName)) return true;
		}
		return false;
	}

	createContent(childrenToAppend?: IJmlSet, attsToAppend?: IJmlObj, guardStack?: SkRuleElt[]): void | string {
		this.subRules.forEach((r, i) => {
			if (CARD.isRequired(CARD.combineCard(this.overSubCard(i, '1'), r.card))) {
				r.createContent(childrenToAppend, attsToAppend, guardStack);
			}
		});
	}

	xAppendInsertables(skCtx: SkContext, listNodes: ISkStructDef[], listAttrs?: ISkStructDef[]) {
		for (let i = 0; i < this.subRules.length; i++) {
			this.subRules[i].xAppendInsertables(skCtx, listNodes, listAttrs);
		}
	}
}

SkRule.registry.register(SkRuleUGroup);

export class SkRuleGroup extends SkRuleDirective {
	static readonly type = ",";

	// @ts-ignore
	get structName() {
		//if (this.uniqueName != null) return this.uniqueName;
		if (!this._builtName) {
			const buf = ["("];
			this.subRules.forEach((r, i) => {
				const card = this.overSubCard(i, '1');
				if (card !== '1') {
					buf.push(r.structName, card, ",");
				} else {
					buf.push(r.structName, ",");
				}
			});
			buf[buf.length - 1] = ")";
			this._builtName = buf.join('');
		}
		return this._builtName;
	}

	// @ts-ignore
	get structType() {return EDirectiveType.group}

	findBestMatching(skCtx: SkContext, matchMode = ESkMatchMode.all, contextCard: ECard = '1') {
		const card = CARD.combineCard(contextCard, this.card);
		const stateIdx = skCtx.reserveRuleState();
		let found: Node[];
		if (matchMode === ESkMatchMode.all) {
			found = this.xFindBestMatchingSub(skCtx, card);
			//Directive racine, tant qu'il reste des nodes inconnus, on les exclut un à un et on recommence.
			while (skCtx.currentChild) {
				skCtx.bindUnknownNodeToRuleAndReset(skCtx.currentChild, this.getUnknownNodeRule(skCtx.skNode, skCtx.currentChild), stateIdx);
				found = this.xFindBestMatchingSub(skCtx, card);
			}
			skCtx.forEachBindableAttr(a => {
				skCtx.bindAttrToRule(a, this.getUnknownAttRule(skCtx.skNode, a.nodeName));
			});
		} else {
			found = this.xFindBestMatchingSub(skCtx, card);
		}
		skCtx.setRuleState(stateIdx, found);
	}

	buildRuleAfter(from: ISkRuleObj, ctxCard: ECard = '1'): SkRule {
		const realCard = CARD.combineCard(this.card, ctxCard);
		if (CARD.isRepeatable(realCard)) return this;
		let result: SkRule;
		if (this.rulesAfter) {
			result = this.rulesAfter.get(from);
			if (result) return result;
		} else {
			this.rulesAfter = new Map();
		}
		//On crée un SkRule dédié avec les rules suivantes.
		let i = 0;
		let firstSubR: SkRule;
		for (; i < this.subRules.length; i++) {
			const subR = this.subRules[i];
			if (subR.findRule((r) => r === from)) {
				//Trouvé
				firstSubR = subR.buildRuleAfter(from, this.overSubCard(i, subR.card));
				if (i === this.subRules.length - 1) result = firstSubR;
				else if (firstSubR === SkRuleEmpty.SINGLETON && i === this.subRules.length - 2) result = this.subRules[this.subRules.length - 1];
				else {
					const rules = [] as SkRule[];
					if (firstSubR !== SkRuleEmpty.SINGLETON) rules.push(firstSubR);
					for (i++; i < this.subRules.length; i++) rules.push(this.subRules[i]);
					result = new SkRuleGroup().initSubRules(rules);
				}
				break;
			}
		}
		if (!result) result = SkRuleEmpty.SINGLETON;
		this.rulesAfter.set(from, result);
		return result;
	}

	/** Mémoire des ruleAfter calculés (voir instanciés) en fonction de chaque from. */
	protected rulesAfter: Map<ISkRuleObj, SkRule>;

	checkSchemaOrder(eltName1: string, eltName2: string): boolean | undefined {
		let pos1 = null;
		let pos2 = null;
		for (let i = 0, s = this.subRules.length; i < s; i++) {
			const subR = this.subRules[i];
			if (pos1 === null && subR.structMatch(ENodeType.element, eltName1)) pos1 = i;
			if (pos2 === null && subR.structMatch(ENodeType.element, eltName2)) pos2 = i;
			if (pos1 !== null && pos2 !== null) {
				if (pos1 === pos2) return subR.checkSchemaOrder(eltName1, eltName2);
				return pos1 < pos2;
			}
		}
		return undefined;
	}

	xFindBestMatchingSub(skCtx: SkContext, card: ECard): null | Node[] {
		let found: Node[]; //1er noeud du groupe.
		if (CARD.isRequired(card) || this.isMatching(skCtx)) {
			//FIXME incorrect pour des groupes d'attributs
			const ch = skCtx.currentChild;
			if (ch) {
				if (!found) found = [ch];
				else found.push(ch);
			}
			for (let i = 0; i < this.subRules.length; i++) {
				this.subRules[i].findBestMatching(skCtx, ESkMatchMode.ordered, this.overSubCard(i, '1'));
			}
		}
		while (CARD.isRepeatable(card) && this.isMatching(skCtx)) {
			//FIXME incorrect pour des groupes d'attributs
			if (!found) found = [skCtx.currentChild];
			else found.push(skCtx.currentChild);
			for (let i = 0; i < this.subRules.length; i++) {
				this.subRules[i].findBestMatching(skCtx, ESkMatchMode.ordered, this.overSubCard(i, '1'));
			}
		}
		return found;
	}

	isMatching(skCtx: SkContext) {
		for (let i = 0; i < this.subRules.length; i++) {
			const rule = this.subRules[i];
			if (rule.isMatching(skCtx)) return true;
			//cette rule ne match pas, si elle est obligaroire, le group ne matche pas.
			if (CARD.isRequired(this.overSubCard(i, rule.card))) return false;
		}
		return false;
	}

	execActions(skCtx: SkContext, contextCard: ECard = '1') {
		const card = CARD.combineCard(contextCard, this.card);
		const opts = skCtx.execOptions;
		const found = skCtx.shiftRuleState() as Node[];
		let addFindOffsetAfter;
		if (found == null || CARD.isRepeatable(card)) {
			if (opts.list1InsertableNodesAt !== undefined) {
				const min = skCtx.currentOffsetMin;
				const max = skCtx.getOffsetMaxAfterCurrent(found ? found.length : 0);
				const insPoint1 = opts.list1InsertableNodesAt;
				if (insPoint1 === -1 || (min <= insPoint1 && max >= insPoint1)) {
					this.xAppendInsertables(skCtx, opts.insertableNodes1);
				}
				const insPoint2 = opts.list2InsertableNodesAt;
				if (insPoint2 >= 0 && min <= insPoint2 && max >= insPoint2) {
					this.xAppendInsertables(skCtx, opts.insertableNodes2);
				}
			}
			if (opts.insertableAttrs) {
				this.xAppendInsertables(skCtx, null, opts.insertableAttrs);
			}
			if (opts.findOffsetNodeType && this.structMatch(opts.findOffsetNodeType, opts.findOffsetNodeName)) {
				switch (CARD.isRepeatable(card) ? opts.findOffsetIfExistAndCardN : 'reject') {
				case "reject":
					opts.findOffset = found ? -1 : skCtx.currentOffset;
					break;
				case "start":
					if (!('findOffset' in opts)) opts.findOffset = skCtx.currentOffset;
					break;
				case "end":
					addFindOffsetAfter = true;
				}
			}
		}
		if (!found) {
			if (CARD.isRequired(card)) {
				// NON, pas de groupMissing : le groupe peut-être required, mais tous ses fils optionnels, on doit descendre dans les fils pour cette eval.
				// if (opts.genAnnots) skCtx.addAnnot(new SkAnnotMissing().init(SkAnnotMissing.TYPE_groupMissing, skCtx.node, skCtx.currentOffset, this));
				this.subRules.forEach((r, i) => {
					r.execActions(skCtx, this.overSubCard(i, '1'));
				});
			} else {
				const importContent = opts.importContent;
				if (importContent) {
					const subCard = importContent instanceof SkRule ? this.getRealCardSubRule(importContent) :
						importContent instanceof Node ? this.getRealCardSubNode(importContent.nodeType, importContent.nodeName) :
							this.getRealCardSubNode(JML.jmlNode2nodeType(importContent), JML.jmlNode2name(importContent));
					if (subCard !== undefined) {
						//importContent est bien autorisé ici.
						if ('pasteOffsetMin' in opts) {
							if (opts.pasteOffsetMin >= skCtx.currentOffsetMin && opts.pasteOffsetMax <= skCtx.currentOffsetMax) {
								opts.pastePos = EPastePos.replace;
							}
						} else {
							opts.importPos = {insertOffsetMin: skCtx.currentOffsetMin, insertOffsetMax: skCtx.currentOffsetMax};
						}
					}
				}
			}
		} else if (skCtx.execOptions.findOffsetNodeType) {
			//On cherche un point d'insertion
			const backup = opts.findOffsetInGroup;
			for (let i = 0; i < found.length; i++) {
				//On parcourt chaque groupe.
				skCtx.currentChild = found[i]; //1er noeud du groupe.
				opts.findOffsetInGroup = 'start';
				this.subRules.forEach((r, i) => {
					let found: boolean;
					const curr = skCtx.currentChild;
					if (curr && r.structMatch(curr.nodeType, curr.nodeName)) {
						opts.findOffsetInGroup = undefined;
						found = true;
					}
					r.execActions(skCtx, this.overSubCard(i, '1'));
					if (found) opts.findOffsetInGroup = 'start';
				});
			}
			opts.findOffsetInGroup = backup; //retsore pour le contexte parent.
		} else {
			for (let i = 0; i < found.length; i++) {
				skCtx.currentChild = found[i];
				this.subRules.forEach((r, i) => {
					r.execActions(skCtx, this.overSubCard(i, '1'))
				});
			}
		}
		if (addFindOffsetAfter) opts.findOffset = skCtx.currentOffsetMax;
	}

	createContent(childrenToAppend?: IJmlSet, attsToAppend?: IJmlObj, guardStack?: SkRuleElt[]): void | string {
		this.subRules.forEach((r, i) => {
			if (CARD.isRequired(CARD.combineCard(this.overSubCard(i, '1'), r.card))) {
				r.createContent(childrenToAppend, attsToAppend, guardStack);
			}
		});
	}

	xAppendInsertables(skCtx: SkContext, listNodes: ISkStructDef[], listAttrs?: ISkStructDef[]) {
		let i = 0;
		if (listNodes) for (; i < this.subRules.length; i++) {
			//On ajoute les rules filles (node et attr) jusqu'au premier node fils obligatoire (hors attributs).
			const rule = this.subRules[i];
			const offs = listNodes.length;
			rule.xAppendInsertables(skCtx, listNodes, listAttrs);
			if (listNodes.length > offs && CARD.isRequired(rule.card)) break;
		}
		if (listAttrs) for (; i < this.subRules.length; i++) {
			//On ajoute tous les autres attributs dans le groupe.
			this.subRules[i].xAppendInsertables(skCtx, null, listAttrs);
		}
	}
}

SkRule.registry.register(SkRuleGroup);

export class SkRuleEmpty extends SkRuleDirective {
	static readonly type = "-";

	subRules = SkRuleEmpty.EMPTY_RULES;

	// @ts-ignore
	get structName() {return ""}

	// @ts-ignore
	get structType() {return EDirectiveType.empty}

	// @ts-ignore
	get structLabel() {return "Vide"}

	linkRules(map: ISkPatternResolver): SkRule {return this}

	isMatching(skCtx: SkContext): boolean {
		//console.log("Empty rule should not be used in complex rules", DOM.debug(skCtx.node));-
		return !skCtx.currentChild && (!(skCtx.node instanceof Element) || skCtx.node.attributes.length === 0);
	}

	findBestMatching(skCtx: SkContext, matchMode = ESkMatchMode.all, contextCard: ECard = '1') {
		if (matchMode === ESkMatchMode.all) {
			skCtx.forEachChild(ch => {
				skCtx.bindNodeToRule(ch, this.getUnknownNodeRule(skCtx.skNode, ch));
			});
			skCtx.forEachAttr(a => {
				skCtx.bindAttrToRule(a, this.getUnknownAttRule(skCtx.skNode, a.nodeName));
			});
		}
	}

	indexPatterns(rulesIndex: SkRule[]) {
		//singleton jamais indexé
	}

	createContent(childrenToAppend?: IJmlSet, attsToAppend?: IJmlObj): void | string {}

	writeTo(o: any, mode: ESerialMode) {}

	readFrom(o: any, mode: ESerialMode): this {
		return SkRuleEmpty.SINGLETON as this;
	}

	static readonly EMPTY_RULES = Object.freeze([]) as SkRule[];
	static readonly SINGLETON = Object.freeze(new SkRuleEmpty());
}

SkRule.registry.register(SkRuleEmpty);

/** Init des SkRule par défaut appelé après chargement des modules pour problème de cycles. */
export function initSkRuleDefaults() {
	if (!Object.isFrozen(SkRuleEltUnknown.DEFAULT)) {
		Object.freeze(SkRuleEltUnknown.DEFAULT.initUnknown());
		Object.freeze(SkRuleAttrUnknown.DEFAULT.initUnknown());
		Object.freeze(SkRuleComment.DEFAULT.initSkMeta(SKMETALIB.getMetaNode('!')));
		SkRuleChoice.ANY.initCard('*').initSubRules([
			new SkRuleElt().init(SkMatcherAnyElt.SINGLETON, '*', SkRuleChoice.ANY).initSkMeta(SKMETALIB.getMetaNode('*')).linkRules(null),
			new SkRuleAttr().init(SkMatcherAnyAttr.SINGLETON, false).initSkMeta(SKMETALIB.getMetaNode('@')),
			SkRuleText.DEFAULT
		]).freezeRule();
	}
}

export function getGenericSchema(): Schema {
	if (!genericSchema) genericSchema = new Schema(new SkRuleDoc().init(SkRuleChoice.ANY.subRules[0]));
	return genericSchema;
}

let genericSchema: Schema;